<template>
  <div class="dashboard-page supervisor-dashboard admin-dashboard-root">

    <!-- =====================================================
         PAGE HEADER & FILTER BAR
    ====================================================== -->
    <div class="dashboard-sticky-area">
      <div class="dashboard-topbar">
        <h4 class="dashboard-page-title">
          <CIcon name="cil-speedometer" class="dashboard-title-icon" />
          {{ dashboardText('แดชบอร์ด', 'Dashboard') }}
        </h4>

        <div class="dashboard-header-right">
          <!-- Global Dashboard Search -->
          <div class="dashboard-search-box" ref="searchBoxRef">
            <CIcon name="cil-search" class="search-icon" />
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="dashboardText('ค้นหา...', 'Search...')"
              class="dashboard-search-input"
              @keydown="onKeyboardSearch" v-listbox-escape="closeKeyboardSearch"
              role="combobox" aria-autocomplete="list" :aria-controls="keyboardSearchId"
              :aria-expanded="String(Boolean(searchQuery && showSearchDropdown))"
              :aria-activedescendant="searchQuery && showSearchDropdown && keyboardSearchIndex >= 0 ? keyboardSearchId + '-' + keyboardSearchIndex : null"
              :aria-label="$t('common.search')"
              @focus="showSearchDropdown = true"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="search-clear-btn" :aria-label="$t('common.clearSearch')"
              @click="clearSearch"
            >
              <CIcon name="cil-x" />
            </button>

            <!-- Search Results Dropdown List -->
            <div
              v-if="searchQuery && showSearchDropdown"
              class="search-dropdown-menu" :id="keyboardSearchId" role="listbox" :aria-label="$t('common.search')"
            >
              <div class="search-dropdown-header">
                <span>{{ dashboardText('ผลการค้นหา (' + searchMatches.length + ' รายการ)', 'Search Results (' + searchMatches.length + ')') }}</span>
                <small v-if="searchMatches.length">{{ dashboardText('คลิกเพื่อไปยังรายการ', 'Click to jump to item') }}</small>
              </div>

              <div v-if="!searchMatches.length" class="p-3 text-center text-muted small">
                {{ dashboardText('ไม่พบข้อมูลที่ตรงกัน (ค้นหาได้ทั้งรหัสอาคาร, ชื่อไทย-อังกฤษ, ห้อง, หรือเลขที่แจ้ง)', 'No matches found (search by building, TH/EN name, room, or ticket #)') }}
              </div>

              <div
                v-for="(m, idx) in searchMatches"
                :key="m.id + '-' + idx"
                class="search-dropdown-item keyboard-listbox-option" :id="keyboardSearchId + '-' + idx" role="option" :aria-selected="String(keyboardSearchIndex === idx)" :data-keyboard-active="keyboardSearchIndex === idx" @mousedown.prevent
                @click="onSelectSearchResult(m)"
              >
                <span class="item-badge" :class="m.badgeClass">{{ m.sectionLabel }}</span>
                <div class="item-content">
                  <div class="item-title">{{ m.title }}</div>
                  <div class="item-desc">{{ m.description }}</div>
                  <small v-if="m.meta" class="text-muted d-block mt-1">{{ m.meta }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Date Range Filter Hotkeys (Right-aligned) -->
          <div class="dashboard-filter">
            <div v-if="selectedRange === 'custom'" class="custom-date-range">
              <input type="date" v-model="startDate" @change="onCustomDateChange" />
              <span>-</span>
              <input type="date" v-model="endDate" @change="onCustomDateChange" />
            </div>

            <div class="range-tabs">
              <button v-for="range in rangeOptions" :key="range.value" type="button" class="range-tab" :class="{
                active: selectedRange === range.value,
                'filter-tab': range.value === 'custom'
              }" @click="onRangeSelect(range.value)">
                <CIcon v-if="range.value === 'custom'" name="cil-filter" />
                <span v-else>{{ range.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- =====================================================
         KPI SUMMARY GRID
    ====================================================== -->
    <div class="kpi-grid mb-3">
      <!-- Total Requests -->
      <div class="summary-card clickable" @click="goToTicketList('all')" v-keyboard-activate role="button" tabindex="0" style="cursor: pointer;">
        <div class="summary-card-top">
          <div class="summary-icon icon-blue">
            <CIcon name="cil-bell" />
          </div>
          <span class="summary-more">•••</span>
        </div>
        <div class="summary-label">{{ dashboardText('จำนวนการแจ้งซ่อมทั้งหมด', 'Total Repair Requests') }}</div>
        <div class="summary-bottom">
          <div class="summary-value">{{ stats.total }}</div>
          <span class="trend-badge trend-neutral">{{ dashboardText('ทั้งหมด', 'TOTAL') }}</span>
        </div>
      </div>

      <!-- In Progress -->
      <div class="summary-card clickable" @click="goToTicketList('in_progress')" v-keyboard-activate role="button" tabindex="0" style="cursor: pointer;">
        <div class="summary-card-top">
          <div class="summary-icon icon-orange">
            <CIcon name="cil-av-timer" />
          </div>
          <span class="summary-more">•••</span>
        </div>
        <div class="summary-label">{{ dashboardText('กำลังดำเนินการ', 'In Progress') }}</div>
        <div class="summary-bottom">
          <div class="summary-value">{{ stats.inProgress }}</div>
          <span class="trend-badge trend-active">{{ dashboardText('กำลังทำ', 'ACTIVE') }}</span>
        </div>
      </div>

      <!-- Overdue SLA -->
      <div class="summary-card clickable" @click="goToTicketList('overdue')" v-keyboard-activate role="button" tabindex="0" style="cursor: pointer;">
        <div class="summary-card-top">
          <div class="summary-icon icon-red">
            <CIcon name="cil-clock" />
          </div>
          <span class="summary-more">•••</span>
        </div>
        <div class="summary-label">{{ dashboardText('งานที่เกินกำหนด SLA', 'Overdue SLA Jobs') }}</div>
        <div class="summary-bottom">
          <div class="summary-value">{{ stats.overdue }}</div>
          <span class="trend-badge" :class="stats.overdue > 0 ? 'trend-down' : 'trend-up'">SLA</span>
        </div>
      </div>

      <!-- Completed -->
      <div class="summary-card clickable" @click="goToTicketList('completed')" v-keyboard-activate role="button" tabindex="0" style="cursor: pointer;">
        <div class="summary-card-top">
          <div class="summary-icon icon-green">
            <CIcon name="cil-check-circle" />
          </div>
          <span class="summary-more">•••</span>
        </div>
        <div class="summary-label">{{ dashboardText('ปิดงานแล้ว', 'Completed') }}</div>
        <div class="summary-bottom">
          <div class="summary-value">{{ stats.completed }}</div>
          <span class="trend-badge trend-up">{{ dashboardText('ปิดงานแล้ว', 'DONE') }}</span>
        </div>
      </div>
    </div>

    <!-- =====================================================
         OPERATIONAL SUMMARY BAR
    ====================================================== -->
    <div class="operational-summary mb-3">
      <div class="overview-panel">
        <div>
          <span class="overview-eyebrow">{{ dashboardText('บุคลากรทั้งหมด', 'Total Staff') }}</span>
          <strong>{{ staff.length }} {{ dashboardText('คน', 'people') }}</strong>
        </div>
      </div>

      <div
        class="overview-panel clickable"
        @click="goToTechnicianWorkload"
        v-keyboard-activate
        role="button"
        tabindex="0"
        style="cursor: pointer;"
        :title="dashboardText('คลิกเพื่อดูภาระงานช่างเทคนิค', 'Click to view technician workload')"
      >
        <div>
          <span class="overview-eyebrow">{{ dashboardText('สถานะช่างเทคนิค', 'Technician Availability') }}</span>
          <strong>{{ technicianAvailabilitySummary.total }} {{ dashboardText('คน', 'technicians') }}</strong>
        </div>
        <div class="overview-metrics">
          <span class="overview-metric metric-within">
            <b>{{ technicianAvailabilitySummary.available }}</b> {{ dashboardText('ว่าง', 'Available') }}
          </span>
          <span class="overview-metric metric-overdue">
            <b>{{ technicianAvailabilitySummary.busy }}</b> {{ dashboardText('ไม่ว่าง', 'Busy') }}
          </span>
        </div>
      </div>
    </div>

    <!-- =====================================================
         BOTTOM SECTIONS: DESKTOP ONLY (HIDDEN ON MOBILE)
    ====================================================== -->
    <div class="d-none d-md-block">
      <!-- =====================================================
           MAIN DASHBOARD NAVIGATION TABS (ZERO-SCROLL HUBS)
      ====================================================== -->
      <div class="dashboard-main-tabs mb-3">
      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'todo' }"
        @click="activeDashboardTab = 'todo'"
      >
        <CIcon name="cil-list" class="mr-1" />
        {{ dashboardText('งานค้าง/รอจัดการ', 'To-Do Jobs') }}
        <span class="tab-badge badge-cyan" v-if="todoList.length">{{ todoList.length }}</span>
      </button>

      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'workload' }"
        @click="activeDashboardTab = 'workload'"
      >
        <CIcon name="cil-people" class="mr-1" />
        {{ dashboardText('ภาระงานทีม', 'Team Workload') }}
      </button>

      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'analytics' }"
        @click="activeDashboardTab = 'analytics'"
      >
        <CIcon name="cil-chart-pie" class="mr-1" />
        {{ dashboardText('สถิติ & กราฟ', 'Analytics') }}
      </button>

      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'parts' }"
        @click="activeDashboardTab = 'parts'"
      >
        <CIcon name="cil-wrench" class="mr-1" />
        {{ dashboardText('อะไหล่ & ใบงาน', 'Parts & Tickets') }}
      </button>

      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'all' }"
        @click="activeDashboardTab = 'all'"
      >
        <CIcon name="cil-grid" class="mr-1" />
        {{ dashboardText('แสดงทั้งหมด', 'Show All') }}
      </button>
    </div>

    <!-- =====================================================
         WORK AREA: TO-DO LIST & SELECTED JOB DETAILS
    ====================================================== -->
    <div v-if="['todo', 'all'].includes(activeDashboardTab)" class="work-layout mb-3" :class="{ 'work-layout-full': !selectedTodo }">
      <!-- To-do List -->
      <div class="work-card">
        <div class="work-card-header d-flex justify-content-between align-items-center">
          <div>
            <h4>{{ dashboardText('งานที่ต้องจัดการ', 'To-do List') }}</h4>
            <p>{{ dashboardText('งานที่รอการจัดการและติดตามผล', 'Jobs requiring attention') }}</p>
          </div>
          <button type="button" class="view-all-button" @click="$router.push('/admin/mainadmin')">
            {{ dashboardText('ดูทั้งหมด', 'View all') }}
          </button>
        </div>

        <div v-if="todoList.length" class="task-table-wrapper">
          <div class="task-table d-none d-md-block">
            <div class="task-table-header">
              <div>{{ dashboardText('เลขที่แจ้งซ่อม', 'Ticket') }}</div>
              <div>{{ dashboardText('ผู้แจ้ง', 'Reporter') }}</div>
              <div>{{ dashboardText('รายละเอียดปัญหา', 'Description') }}</div>
              <div>{{ dashboardText('สถานะ', 'Status') }}</div>
              <div></div>
            </div>

            <div v-for="task in paginatedTodoTickets" :key="task._id" :id="'todo-' + (task._id || task.ticketNumber)" class="task-table-row"
              :class="{ selected: selectedTodo && selectedTodo._id === task._id }" @click="selectTodo(task)">
              <div>
                <div class="ticket-number">{{ task.ticketNumber || "-" }}</div>
                <small>{{ formatDate(task.userPart && task.userPart.dateTimeInformed ? task.userPart.dateTimeInformed :
                  task.createdAt) }}</small>
              </div>

              <div class="reporter-cell">
                <div class="reporter-avatar">
                  <CIcon name="cil-user" />
                </div>
                <div>{{ getReporterName(task) }}</div>
              </div>

              <div class="task-description">
                {{ task.userPart && task.userPart.problemDetails ? task.userPart.problemDetails : "-" }}
              </div>

              <div class="todo-status-cell">
                <div class="todo-status-badges">
                  <span class="soft-status" :class="getWorkflowStatusClass(task.status)">
                    {{ getStatusLabel(task.status) }}
                  </span>
                  <span class="sla-status-pill" :class="getSlaPillInfo(task).className">
                    {{ getSlaPillInfo(task).text }}
                  </span>
                </div>
              </div>

              <div>
                <button type="button" class="row-action" @click.stop="selectTodo(task)"
                  :title="dashboardText('ดูรายละเอียด', 'View Details')">
                  <CIcon name="cil-chevron-right" />
                </button>
              </div>
            </div>
          </div>

          <!-- Mobile Ticket Cards (media max-width: 768px) -->
          <div class="mobile-ticket-card-list d-md-none">
            <div
              v-for="task in paginatedTodoTickets"
              :key="'m-todo-' + task._id"
              :id="'m-todo-' + (task._id || task.ticketNumber)"
              class="mobile-ticket-card"
              @click="$router.push('/admin/mainadmin')"
            >
              <div class="mobile-ticket-card-header">
                <span class="mobile-ticket-id">{{ task.ticketNumber || "-" }}</span>
                <span class="mobile-ticket-badge" :class="getWorkflowStatusClass(task.status)">
                  {{ getStatusLabel(task.status) }}
                </span>
              </div>
              <div class="mobile-ticket-card-body">
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('ผู้แจ้ง', 'Requester') }}</span>
                  <span class="mobile-row-val">{{ getReporterName(task) }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('สังกัด', 'Affiliation') }}</span>
                  <span class="mobile-row-val">{{ getReporterAffiliation(task) }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('สถานที่', 'Location') }}</span>
                  <span class="mobile-row-val">{{ getTaskLocation(task) }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('ประเภทงาน', 'Category') }}</span>
                  <span class="mobile-row-val">
                    {{
                      task.userPart && task.userPart.subCategory && typeof task.userPart.subCategory === 'object'
                        ? task.userPart.subCategory.name || '-'
                        : task.userPart && task.userPart.subCategory
                          ? task.userPart.subCategory
                          : '-'
                    }}
                  </span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('รายละเอียดปัญหา', 'Problem Details') }}</span>
                  <span class="mobile-row-val">{{ task.userPart && task.userPart.problemDetails ? task.userPart.problemDetails : '-' }}</span>
                </div>
              </div>
              <button type="button" class="mobile-view-details-btn" @click.stop="$router.push('/admin/mainadmin')">
                {{ dashboardText('ดูรายละเอียด', 'View Details') }}
              </button>
            </div>
          </div>

          <div v-if="todoTotalPages > 1" class="todo-pagination mt-3">
            <button type="button" class="todo-page-button" :disabled="todoCurrentPage === 1"
              @click="todoCurrentPage = 1">«</button>
            <button type="button" class="todo-page-button" :disabled="todoCurrentPage === 1"
              @click="todoCurrentPage--">‹</button>
            <button v-for="page in todoPageNumbers" :key="page" type="button" class="todo-page-button"
              :class="{ active: todoCurrentPage === page }" @click="todoCurrentPage = page">{{ page }}</button>
            <button type="button" class="todo-page-button" :disabled="todoCurrentPage === todoTotalPages"
              @click="todoCurrentPage++">›</button>
            <button type="button" class="todo-page-button" :disabled="todoCurrentPage === todoTotalPages"
              @click="todoCurrentPage = todoTotalPages">»</button>
          </div>
        </div>

        <div v-else class="task-empty text-center py-4">
          <CIcon name="cil-inbox" size="2xl" class="mb-2 text-muted" />
          <div class="text-muted">{{ dashboardText('ไม่มีงานค้างรอจัดการ', 'No pending tasks') }}</div>
        </div>
      </div>

      <!-- Job Details Card -->
      <div v-if="selectedTodo" class="work-card detail-card">
        <div class="work-card-header detail-header d-flex justify-content-between align-items-center">
          <div>
            <h4>{{ dashboardText('รายละเอียดงาน', 'Job Details') }}</h4>
            <p class="selected-ticket-number">{{ selectedTodo.ticketNumber }}</p>
          </div>
          <div class="sup-detail-header-right d-flex align-items-center">
            <span class="soft-status"
              :class="selectedTodo.status === 'pending' ? 'soft-status-pending' : 'soft-status-active'">
              {{ getStatusLabel(selectedTodo.status) }}
            </span>
            <button type="button" class="sup-close-detail-btn ml-3" @click="selectedTodo = null" :title="dashboardText('ปิดรายละเอียด', 'Close Details')">
              <CIcon name="cil-x" />
            </button>
          </div>
        </div>

        <div class="selected-job-content">
          <div class="detail-grid">
            <div class="detail-block">
              <span>{{ dashboardText('ผู้แจ้ง', 'Requester') }}</span>
              <strong>{{ getReporterName(selectedTodo) }}</strong>
            </div>

            <div class="detail-block">
              <span>{{ dashboardText('สังกัด', 'Affiliation') }}</span>
              <strong>{{ getReporterAffiliation(selectedTodo) }}</strong>
            </div>

            <div class="detail-block">
              <span>{{ dashboardText('สถานที่', 'Location') }}</span>
              <strong>{{ getTaskLocation(selectedTodo) }}</strong>
            </div>

            <div class="detail-block">
              <span>{{ dashboardText('ประเภทงาน', 'Category') }}</span>
              <strong>
                {{
                  selectedTodo.userPart && selectedTodo.userPart.subCategory && typeof selectedTodo.userPart.subCategory
                    === "object"
                    ? selectedTodo.userPart.subCategory.name || "-"
                    : selectedTodo.userPart && selectedTodo.userPart.subCategory
                      ? selectedTodo.userPart.subCategory
                      : "-"
                }}
              </strong>
            </div>
          </div>

          <div class="detail-block problem-block mt-3">
            <span>{{ dashboardText('รายละเอียดปัญหา', 'Problem Details') }}</span>
            <p>{{ selectedTodo.userPart && selectedTodo.userPart.problemDetails ? selectedTodo.userPart.problemDetails :
              "-" }}</p>
          </div>
        </div>

        <div class="detail-footer mt-3">
          <button type="button" class="primary-action-button" @click="$router.push('/admin/mainadmin')">
            {{ dashboardText('ดูรายละเอียดทั้งหมดในระบบ', 'View Details') }}
          </button>
        </div>
      </div>
    </div>

    <!-- =====================================================
         TEAM WORKLOAD CARD (SUPERVISOR & TECHNICIAN)
    ====================================================== -->
    <div v-if="['workload', 'all'].includes(activeDashboardTab)" class="work-card workload-card mb-3">
      <div class="work-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4>{{ dashboardText('ภาระงานของทีม', 'Team Workload Overview') }}</h4>
          <p>{{ dashboardText('ภาพรวมภาระงานของซูเปอร์ไวเซอร์และช่างเทคนิคทั้งหมดในช่วงเวลาที่เลือก', 'Overview of workload for supervisors and technicians in the selected period') }}</p>
        </div>
        <div class="workload-tab-segmented">
          <button
            type="button"
            class="segmented-btn"
            :class="{ active: activeWorkloadTab === 'supervisor' }"
            @click="activeWorkloadTab = 'supervisor'"
          >
            <CIcon name="cil-user" class="mr-1" />
            {{ dashboardText('ซูเปอร์ไวเซอร์', 'Supervisors') }} ({{ supervisorWorkloadList.length }})
          </button>
          <button
            type="button"
            class="segmented-btn"
            :class="{ active: activeWorkloadTab === 'technician' }"
            @click="activeWorkloadTab = 'technician'"
          >
            <CIcon name="cil-wrench" class="mr-1" />
            {{ dashboardText('ช่างเทคนิค', 'Technicians') }} ({{ technicianWorkload.length }})
          </button>
        </div>
      </div>

      <!-- SUPERVISOR TAB CONTENT -->
      <div v-if="activeWorkloadTab === 'supervisor'">
        <div class="technician-workload-wrapper d-none d-md-block">
          <table class="technician-workload-table supervisor-workload-table">
            <thead>
              <tr>
                <th>{{ dashboardText('Supervisor', 'Supervisor') }}</th>
                <th>{{ dashboardText('ฝ่าย/แผนก', 'Department / Section') }}</th>
                <th>{{ dashboardText('งานที่ได้รับมอบหมาย', 'Assigned Jobs') }}</th>
                <th>{{ dashboardText('ตอบรับแล้ว', 'Accepted') }}</th>
                <th>{{ dashboardText('กำลังดำเนินการ', 'In Progress') }}</th>
                <th>{{ dashboardText('เสร็จแล้ว', 'Completed') }}</th>
                <th>{{ dashboardText('ภาระงาน', 'Workload') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sup in supervisorWorkloadList" :key="sup.id" :id="'sup-' + sup.id">
                <td :data-label="dashboardText('Supervisor', 'Supervisor')">
                  <strong>{{ sup.name }}</strong>
                </td>
                <td :data-label="dashboardText('ฝ่าย/แผนก', 'Department / Section')">
                  <span class="workload-role">{{ dashboardText('Supervisor', 'Supervisor') }}</span>
                  <small v-if="sup.section" class="workload-section ml-1">{{ formatSection(sup.section) }}</small>
                </td>
                <td :data-label="dashboardText('งานที่ได้รับมอบหมาย', 'Assigned Jobs')">{{ sup.assignedJobs }}</td>
                <td :data-label="dashboardText('ตอบรับแล้ว', 'Accepted')">{{ sup.acceptedJobs }}</td>
                <td :data-label="dashboardText('กำลังดำเนินการ', 'In Progress')">{{ sup.inProgressJobs }}</td>
                <td :data-label="dashboardText('เสร็จแล้ว', 'Completed')">{{ sup.completedJobs }}</td>
                <td :data-label="dashboardText('ภาระงาน', 'Workload')">
                  <div class="workload-progress">
                    <span><i :style="{ width: (Number(sup.workloadPercent) || 0) + '%' }"></i></span>
                    <b>{{ Number(sup.workloadPercent) || 0 }}%</b>
                  </div>
                </td>
              </tr>
              <tr v-if="!supervisorWorkloadList.length">
                <td colspan="7" class="technician-workload-empty">
                  {{ dashboardText('ไม่พบข้อมูลซูเปอร์ไวเซอร์', 'Supervisor data unavailable') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Supervisor Workload Cards (media max-width: 768px) -->
        <div class="mobile-ticket-card-list d-md-none">
          <div v-for="sup in supervisorWorkloadList" :key="'m-sup-' + sup.id" :id="'m-sup-' + sup.id" class="mobile-ticket-card">
            <div class="mobile-ticket-card-header">
              <span class="mobile-ticket-id">{{ sup.name }}</span>
              <span class="mobile-ticket-badge badge-role">{{ dashboardText('Supervisor', 'Supervisor') }}</span>
            </div>
            <div class="mobile-ticket-card-body">
              <div class="mobile-card-row">
                <span class="mobile-row-label">{{ dashboardText('ฝ่าย/แผนก', 'Department / Section') }}</span>
                <span class="mobile-row-val">{{ formatSection(sup.section) }}</span>
              </div>
              <div class="d-flex justify-content-between flex-wrap gap-2">
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('งานที่ได้รับมอบหมาย', 'Assigned') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ sup.assignedJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('ตอบรับแล้ว', 'Accepted') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ sup.acceptedJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('กำลังดำเนินการ', 'In Progress') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ sup.inProgressJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('เสร็จแล้ว', 'Completed') }}</span>
                  <span class="mobile-row-val font-weight-bold text-success">{{ sup.completedJobs }}</span>
                </div>
              </div>
              <div class="mobile-card-row mt-1">
                <span class="mobile-row-label">{{ dashboardText('ภาระงาน', 'Workload') }}</span>
                <div class="workload-progress mt-1">
                  <span><i :style="{ width: (Number(sup.workloadPercent) || 0) + '%' }"></i></span>
                  <b>{{ Number(sup.workloadPercent) || 0 }}%</b>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!supervisorWorkloadList.length" class="technician-workload-empty text-center py-3">
            {{ dashboardText('ไม่พบข้อมูลซูเปอร์ไวเซอร์', 'Supervisor data unavailable') }}
          </div>
        </div>
      </div>

      <!-- TECHNICIAN TAB CONTENT -->
      <div v-else>
        <div class="technician-workload-wrapper d-none d-md-block">
          <table class="technician-workload-table">
            <thead>
              <tr>
                <th>{{ dashboardText('ช่าง', 'Technician') }}</th>
                <th>{{ dashboardText('สถานะ', 'Availability') }}</th>
                <th>{{ dashboardText('งานปัจจุบัน', 'Current Tickets') }}</th>
                <th>{{ dashboardText('ทั้งหมด', 'Total') }}</th>
                <th>{{ dashboardText('กำลังทำ', 'Active') }}</th>
                <th>{{ dashboardText('เสร็จแล้ว', 'Completed') }}</th>
                <th>{{ dashboardText('เกิน SLA', 'Over SLA') }}</th>
                <th>{{ dashboardText('ภาระงาน', 'Workload') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="technician in paginatedTechnicianWorkload" :key="technician._id" :id="'tech-' + technician._id">
                <td :data-label="dashboardText('ช่าง', 'Technician')">
                  <strong>{{ technician.name }}</strong>
                </td>
                <td :data-label="dashboardText('สถานะ', 'Availability')">
                  <span class="technician-availability" :class="technician.availabilityClass">
                    {{ dashboardText(technician.activeJobs > 0 ? 'ไม่ว่าง' : 'ว่าง', technician.availability) }}
                  </span>
                </td>
                <td :data-label="dashboardText('งานปัจจุบัน', 'Current Tickets')">
                  <div v-if="technician.currentTickets.length" class="technician-current-tickets">
                    <div v-for="item in technician.currentTickets" :key="item.id" class="current-ticket-line">
                      <span><b>{{ item.number }}</b> — {{ item.jobType }}</span>
                      <span class="current-ticket-status" :class="item.sla.className">{{ item.sla.text }}</span>
                    </div>
                    <small v-if="technician.remainingCurrentTickets">+{{ technician.remainingCurrentTickets }} {{dashboardText('งาน', 'jobs') }}</small>
                  </div>
                  <span v-else class="no-current-ticket">{{ dashboardText('ไม่มีงานปัจจุบัน', 'No current tickets')
                  }}</span>
                </td>
                <td :data-label="dashboardText('ทั้งหมด', 'Total')">{{ technician.totalJobs }}</td>
                <td :data-label="dashboardText('กำลังทำ', 'Active')">{{ technician.activeJobs }}</td>
                <td :data-label="dashboardText('เสร็จแล้ว', 'Completed')">{{ technician.completedJobs }}</td>
                <td :data-label="dashboardText('เกิน SLA', 'Over SLA')">{{ technician.overdueJobs }}</td>
                <td :data-label="dashboardText('ภาระงาน', 'Workload')">
                  <div class="workload-progress">
                    <span><i :style="{ width: (Number(technician.workloadPercent) || 0) + '%' }"></i></span>
                    <b>{{ Number(technician.workloadPercent) || 0 }}%</b>
                  </div>
                </td>
              </tr>
              <tr v-if="!technicianWorkload.length">
                <td colspan="8" class="technician-workload-empty">
                  {{ dashboardText('ไม่พบข้อมูลช่างเทคนิค', 'No technicians found') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Technician Workload Cards (media max-width: 768px) -->
        <div class="mobile-ticket-card-list d-md-none">
          <div v-for="technician in paginatedTechnicianWorkload" :key="'m-tech-' + technician._id" :id="'m-tech-' + technician._id" class="mobile-ticket-card">
            <div class="mobile-ticket-card-header">
              <span class="mobile-ticket-id">{{ technician.name }}</span>
              <span class="technician-availability" :class="technician.availabilityClass">
                {{ dashboardText(technician.activeJobs > 0 ? 'ไม่ว่าง' : 'ว่าง', technician.availability) }}
              </span>
            </div>
            <div class="mobile-ticket-card-body">
              <div class="mobile-card-row">
                <span class="mobile-row-label">{{ dashboardText('งานปัจจุบัน', 'Current Tickets') }}</span>
                <div v-if="technician.currentTickets.length" class="technician-current-tickets mt-1">
                  <div v-for="item in technician.currentTickets" :key="item.id" class="current-ticket-line">
                    <span><b>{{ item.number }}</b> — {{ item.jobType }}</span>
                    <span class="current-ticket-status" :class="item.sla.className">{{ item.sla.text }}</span>
                  </div>
                  <small v-if="technician.remainingCurrentTickets">+{{ technician.remainingCurrentTickets }} {{ dashboardText('งาน', 'jobs') }}</small>
                </div>
                <span v-else class="text-muted">{{ dashboardText('ไม่มีงานปัจจุบัน', 'No current tickets') }}</span>
              </div>
              <div class="d-flex justify-content-between flex-wrap gap-2">
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('ทั้งหมด', 'Total') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ technician.totalJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('กำลังทำ', 'Active') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ technician.activeJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('เสร็จแล้ว', 'Completed') }}</span>
                  <span class="mobile-row-val font-weight-bold text-success">{{ technician.completedJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('เกิน SLA', 'Over SLA') }}</span>
                  <span class="mobile-row-val font-weight-bold text-danger">{{ technician.overdueJobs }}</span>
                </div>
              </div>
              <div class="mobile-card-row mt-1">
                <span class="mobile-row-label">{{ dashboardText('ภาระงาน', 'Workload') }}</span>
                <div class="workload-progress mt-1">
                  <span><i :style="{ width: (Number(technician.workloadPercent) || 0) + '%' }"></i></span>
                  <b>{{ Number(technician.workloadPercent) || 0 }}%</b>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!technicianWorkload.length" class="technician-workload-empty text-center py-3">
            {{ dashboardText('ไม่พบข้อมูลช่างเทคนิค', 'No technicians found') }}
          </div>
        </div>

        <div v-if="technicianTotalPages > 1" class="todo-pagination technician-pagination mt-3">
          <button type="button" class="todo-page-button" :disabled="technicianCurrentPage === 1"
            @click="technicianCurrentPage = 1">«</button>
          <button type="button" class="todo-page-button" :disabled="technicianCurrentPage === 1"
            @click="technicianCurrentPage--">‹</button>
          <button v-for="page in technicianPageNumbers" :key="page" type="button" class="todo-page-button"
            :class="{ active: technicianCurrentPage === page }" @click="technicianCurrentPage = page">{{ page }}</button>
          <button type="button" class="todo-page-button" :disabled="technicianCurrentPage === technicianTotalPages"
            @click="technicianCurrentPage++">›</button>
          <button type="button" class="todo-page-button" :disabled="technicianCurrentPage === technicianTotalPages"
            @click="technicianCurrentPage = technicianTotalPages">»</button>
        </div>
      </div>
    </div>

    <!-- =====================================================
         PARTS / EQUIPMENT OVERVIEW
    ====================================================== -->
    <div v-if="['parts', 'all'].includes(activeDashboardTab)" class="work-card parts-overview-card mb-3">
      <div class="work-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 class="m-0 font-weight-bold">{{ dashboardText('อะไหล่ / อุปกรณ์ที่ใช้', 'Parts / Equipment Used') }}</h4>
          <p class="m-0 text-muted small">{{ dashboardText('รายการและค่าใช้จ่ายจากงานในช่วงเวลาที่เลือก', 'Parts and costs for tickets in the selected period') }}</p>
        </div>
      </div>

      <!-- TABLE FORMAT FOR PARTS DATA -->
      <div class="technician-workload-wrapper d-none d-md-block">
        <table class="technician-workload-table parts-workload-table">
          <thead>
            <tr>
              <th>{{ dashboardText('เลขที่แจ้งซ่อม', 'Ticket No.') }}</th>
              <th>{{ dashboardText('ประเภทงาน', 'Job Type') }}</th>
              <th class="text-center">{{ dashboardText('รายการอะไหล่ / อุปกรณ์', 'Parts / Equipment') }}</th>
              <th class="text-center">{{ dashboardText('จำนวนรวม', 'Total Qty') }}</th>
              <th class="text-right">{{ dashboardText('รวมทั้งสิ้น (บาท)', 'Total (THB)') }}</th>
              <th class="text-center">{{ dashboardText('การจัดการ', 'Actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in paginatedPartsTickets" :key="ticket.id" :id="'parts-' + ticket.id">
              <td :data-label="dashboardText('เลขที่แจ้งซ่อม', 'Ticket No.')">
                <strong>{{ ticket.number }}</strong>
              </td>
              <td :data-label="dashboardText('ประเภทงาน', 'Job Type')">
                <span>{{ ticket.jobType }}</span>
              </td>
              <td :data-label="dashboardText('รายการอะไหล่ / อุปกรณ์', 'Parts / Equipment')" class="text-center">
                <div v-if="ticket.parts.length" class="parts-inline-list">
                  <span v-for="(part, index) in ticket.parts.slice(0, 3)" :key="index" class="parts-inline-tag">
                    {{ part.name }} (x{{ part.quantity }})
                  </span>
                  <small v-if="ticket.parts.length > 3" class="parts-more-count ml-1">+{{ ticket.parts.length - 3 }} {{
                    dashboardText('รายการ', 'items') }}</small>
                </div>
                <span v-else class="text-muted font-italic">{{ dashboardText('ไม่ใช้อะไหล่', 'No parts used') }}</span>
              </td>
              <td :data-label="dashboardText('จำนวนรวม', 'Total Qty')" class="text-center">
                <span class="parts-qty-badge">{{ ticket.parts.reduce((sum, p) => sum + p.quantity, 0) }}</span>
              </td>
              <td :data-label="dashboardText('รวมทั้งสิ้น (บาท)', 'Total (THB)')" class="text-right">
                <span class="parts-price-val" :class="{ 'has-cost': ticket.total > 0 }">{{ formatCurrency(ticket.total) }}</span>
              </td>
              <td :data-label="dashboardText('การจัดการ', 'Actions')" class="text-center">
                <button type="button" class="parts-detail-button" @click="openPartsDetail(ticket)">
                  {{ dashboardText('ดูรายละเอียด', 'View Details') }}
                </button>
              </td>
            </tr>
            <tr v-if="!partsTickets.length">
              <td colspan="6" class="technician-workload-empty parts-empty">
                {{ dashboardText('ไม่พบข้อมูลอะไหล่ในช่วงเวลานี้', 'No parts data for this period') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Parts Cards (media max-width: 768px) -->
      <div class="mobile-ticket-card-list d-md-none">
        <div v-for="ticket in paginatedPartsTickets" :key="'m-part-' + ticket.id" :id="'m-part-' + ticket.id" class="mobile-ticket-card">
          <div class="mobile-ticket-card-header">
            <span class="mobile-ticket-id">{{ ticket.number }}</span>
            <span class="mobile-job-type-pill">{{ ticket.jobType }}</span>
          </div>
          <div class="mobile-ticket-card-body">
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('รายการอะไหล่ / อุปกรณ์', 'Parts / Equipment') }}</span>
              <div v-if="ticket.parts.length" class="parts-inline-list mt-1">
                <span v-for="(part, index) in ticket.parts.slice(0, 3)" :key="index" class="parts-inline-tag">
                  {{ part.name }} (x{{ part.quantity }})
                </span>
                <small v-if="ticket.parts.length > 3" class="parts-more-count ml-1">+{{ ticket.parts.length - 3 }} {{ dashboardText('รายการ', 'items') }}</small>
              </div>
              <span v-else class="text-muted font-italic">{{ dashboardText('ไม่ใช้อะไหล่', 'No parts used') }}</span>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-2">
              <div class="mobile-card-row mb-0">
                <span class="mobile-row-label">{{ dashboardText('จำนวนรวม', 'Total Qty') }}</span>
                <span class="mobile-row-val font-weight-bold">{{ ticket.parts.reduce((sum, p) => sum + p.quantity, 0) }}</span>
              </div>
              <div class="mobile-card-row mb-0">
                <span class="mobile-row-label">{{ dashboardText('รวมทั้งสิ้น', 'Total') }}</span>
                <span class="mobile-row-val font-weight-bold text-dark">{{ formatCurrency(ticket.total) }} {{ dashboardText('บาท', 'THB') }}</span>
              </div>
            </div>
          </div>
          <button type="button" class="mobile-view-details-btn" @click="openPartsDetail(ticket)">
            {{ dashboardText('ดูรายละเอียด', 'View Details') }}
          </button>
        </div>
        <div v-if="!partsTickets.length" class="technician-workload-empty text-center py-3">
          {{ dashboardText('ไม่พบข้อมูลอะไหล่ในช่วงเวลานี้', 'No parts data for this period') }}
        </div>
      </div>

      <!-- Parts Table Footer Strip -->
      <div class="parts-table-footer d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div v-if="partsTotalPages > 1" class="todo-pagination m-0">
          <button type="button" class="todo-page-button" :disabled="partsCurrentPage === 1"
            @click="partsCurrentPage = 1">«</button>
          <button type="button" class="todo-page-button" :disabled="partsCurrentPage === 1"
            @click="partsCurrentPage--">‹</button>
          <button v-for="page in partsPageNumbers" :key="page" type="button" class="todo-page-button"
            :class="{ active: partsCurrentPage === page }" @click="partsCurrentPage = page">{{ page }}</button>
          <button type="button" class="todo-page-button" :disabled="partsCurrentPage === partsTotalPages"
            @click="partsCurrentPage++">›</button>
          <button type="button" class="todo-page-button" :disabled="partsCurrentPage === partsTotalPages"
            @click="partsCurrentPage = partsTotalPages">»</button>
        </div>
        <div v-else></div>

        <div class="parts-summary-strip d-flex align-items-center gap-3">
          <span class="parts-summary-chip">
            <span class="chip-label mr-1">{{ dashboardText('งานที่ใช้อะไหล่:', 'Tickets with parts:') }}</span>
            <b class="chip-value">{{ partsSummary.ticketsWithParts }}</b>
          </span>
          <span class="parts-summary-chip">
            <span class="chip-label mr-1">{{ dashboardText('รายการอะไหล่รวม:', 'Total items:') }}</span>
            <b class="chip-value">{{ partsSummary.itemCount }}</b>
          </span>
          <span class="parts-summary-chip parts-summary-total">
            <span class="chip-label mr-1">{{ dashboardText('ยอดรวมทั้งสิ้น:', 'Grand Total:') }}</span>
            <b class="chip-value-total">{{ formatCurrency(partsSummary.totalCost) }}</b>
            <span class="chip-unit ml-1">{{ dashboardText('บาท', 'THB') }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- =====================================================
         MAIN ANALYTICS & CHARTS
    ====================================================== -->
    <div v-if="['analytics', 'all'].includes(activeDashboardTab)" class="analytics-section-wrapper">
      <!-- Secondary Charts: Department Pie & Monthly Bar -->
      <CRow class="mb-3 secondary-charts-row">
        <CCol md="6" class="secondary-chart-col">
          <CCard class="h-100 shadow-sm border-0 secondary-chart-card">
            <CCardHeader class="bg-white font-weight-bold">
              <CIcon name="cil-chart-pie" class="mr-2 text-info" /> {{ dashboardText('สัดส่วนงานแจ้งซ่อมตามหน่วยงาน',
                'Repair Requests by Department') }}
            </CCardHeader>
            <CCardBody class="chart-card-body">
              <div class="pie-chart-wrapper">
                <CChartPie :datasets="pieDatasets" :labels="pieLabels" :options="pieOptions" />
              </div>
              <div v-if="pieLabels.length === 0" class="text-center mt-3 text-muted">
                <small>{{ dashboardText('ไม่พบข้อมูล', 'No Data Available') }}</small>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md="6" class="secondary-chart-col">
          <CCard class="h-100 shadow-sm border-0 secondary-chart-card">
            <CCardHeader class="bg-white font-weight-bold">
              <CIcon name="cil-chart-line" class="mr-2 text-info" /> {{ dashboardText('สถิติการแจ้งซ่อมรายเดือน',
                'Monthly Repair Statistics') }}
            </CCardHeader>
            <CCardBody class="chart-card-body">
              <div class="bar-chart-wrapper">
                <CChartBar :datasets="barDatasets" :labels="barLabels" :options="barOptions" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>

    <!-- Parts Detail Modal -->
    <CModal
      :show.sync="showPartsDetailModal"
      class="parts-detail-modal"
      centered
      size="lg"
    >
      <template #header>
        <div class="parts-modal-header d-flex justify-content-between align-items-center w-100">
          <div>
            <h5 class="parts-modal-title mb-1 font-weight-bold">
              {{ dashboardText('รายละเอียดอะไหล่ / อุปกรณ์', 'Parts & Equipment Details') }}
            </h5>
            <p class="parts-modal-subtitle mb-0 text-muted">
              {{ dashboardText('ข้อมูลรายการอะไหล่และค่าใช้จ่ายของใบงาน', 'Parts breakdown and itemized costs') }}
            </p>
          </div>
          <button type="button" class="parts-modal-close-btn" @click="showPartsDetailModal = false" aria-label="Close">
            &times;
          </button>
        </div>
      </template>

      <div v-if="selectedPartsTicket" class="parts-detail-content">
        <!-- Ticket Info Strip -->
        <div class="parts-modal-info-bar">
          <div class="parts-info-col">
            <span class="parts-info-label">{{ dashboardText('เลขที่แจ้งซ่อม', 'Ticket No.') }}</span>
            <strong class="parts-info-value ticket-id">{{ selectedPartsTicket.number }}</strong>
          </div>
          <div class="parts-info-col">
            <span class="parts-info-label">{{ dashboardText('ประเภทงาน', 'Job Type') }}</span>
            <span class="parts-info-value">{{ selectedPartsTicket.jobType }}</span>
          </div>
          <div class="parts-info-col text-right">
            <span class="parts-info-label">{{ dashboardText('จำนวนรายการ', 'Total Items') }}</span>
            <span class="parts-info-value">{{ selectedPartsTicket.parts.length }} {{ dashboardText('รายการ', 'items') }}</span>
          </div>
        </div>

        <!-- Parts Table in Modal (when parts exist) -->
        <div v-if="selectedPartsTicket.parts.length" class="parts-modal-table-container">
          <table class="parts-modal-table">
            <thead>
              <tr>
                <th class="th-num">#</th>
                <th class="th-name">{{ dashboardText('รายการอะไหล่ / อุปกรณ์', 'Part / Equipment Name') }}</th>
                <th class="th-qty text-center">{{ dashboardText('จำนวน', 'Qty') }}</th>
                <th class="th-price text-right">{{ dashboardText('ราคาต่อหน่วย (บาท)', 'Unit Price (THB)') }}</th>
                <th class="th-total text-right">{{ dashboardText('รวม (บาท)', 'Total (THB)') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(part, index) in selectedPartsTicket.parts" :key="selectedPartsTicket.id + '-modal-part-' + index">
                <td class="td-num">{{ index + 1 }}</td>
                <td class="td-name font-weight-bold">{{ part.name }}</td>
                <td class="td-qty text-center">{{ part.quantity }}</td>
                <td class="td-price text-right">{{ formatCurrency(part.unitPrice) }}</td>
                <td class="td-total text-right font-weight-bold">{{ formatCurrency(part.total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State (No parts used) -->
        <div v-else class="parts-modal-empty-state">
          <p class="mb-0 text-muted">{{ dashboardText('ไม่มีการใช้อะไหล่สำหรับงานนี้', 'No parts used for this ticket') }}</p>
        </div>

        <!-- Total Cost Bar -->
        <div class="parts-modal-summary-card">
          <span class="parts-summary-title font-weight-bold">
            {{ dashboardText('ยอดรวมค่าอะไหล่ทั้งสิ้น', 'Total Parts Cost') }}
          </span>
          <div class="parts-summary-amount">
            <strong>{{ formatCurrency(selectedPartsTicket.total) }}</strong>
            <span class="parts-currency-unit">{{ dashboardText('บาท', 'THB') }}</span>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="w-100 d-flex justify-content-end">
          <button type="button" class="parts-modal-close-action-btn" @click="showPartsDetailModal = false">
            {{ dashboardText('ปิด', 'Close') }}
          </button>
        </div>
      </template>
    </CModal>



    <!-- =====================================================
         DETAILED TICKET TABLE
    ====================================================== -->
    <div v-if="['parts', 'all'].includes(activeDashboardTab)" class="work-card report-table-card">
      <div class="work-card-header d-flex justify-content-between align-items-center flex-wrap">
        <h4 class="mb-0 py-1">
          <CIcon name="cil-list" class="mr-2 text-primary" /> {{ dashboardText('รายละเอียดใบงาน', 'Ticket Details') }}
        </h4>
      </div>

      <div class="report-table-wrapper d-none d-md-block">
        <CDataTable :items="formattedTickets" :fields="reportFields" column-filter :table-filter="{
          label: dashboardText('ค้นหา:', 'Filter:'),
          placeholder: dashboardText('พิมพ์คำค้นหา...', 'Type to filter...')
        }" sorter cleaner hover pagination :active-page.sync="tableCurrentPage" :items-per-page.sync="tableItemsPerPage" :items-per-page-select="{
          label: dashboardText('แสดงต่อหน้า:', 'Items per page:'),
          values: [10, 20, 50]
        }">
          <template #ticketNo="{ item }">
            <td :id="'ticket-row-' + item.ticketNo" class="font-weight-bold">{{ item.ticketNo }}</td>
          </template>

          <template #reporterDepartment="{ item }">
            <td>{{ item.reporterDept }}</td>
          </template>

          <template #status="{ item }">
            <td>
              <CBadge :color="getBadge(item._status_raw)">
                {{ item.status }}
              </CBadge>
            </td>
          </template>

          <template #sla="{ item }">
            <td>
              <CBadge :color="item._slaOverdue ? 'danger' : 'success'">
                {{ item.sla }}
              </CBadge>
              <small v-if="item._sla_hours" class="d-block text-muted">
                {{ dashboardText('เป้าหมาย SLA:', 'SLA Target:') }} {{ item._sla_hours }}h
              </small>
            </td>
          </template>
        </CDataTable>
      </div>

      <!-- Mobile Ticket Cards (media max-width: 768px) -->
      <div class="mobile-ticket-card-list d-md-none">
        <div
          v-for="(item, idx) in formattedTickets"
          :key="'m-rep-' + item.ticketNo + '-' + idx"
          :id="'m-rep-' + item.ticketNo + '-' + idx"
          class="mobile-ticket-card"
          @click="$router.push('/admin/mainadmin')"
        >
          <div class="mobile-ticket-card-header">
            <span class="mobile-ticket-id">{{ item.ticketNo }}</span>
            <span class="mobile-ticket-badge" :class="'badge-' + getBadge(item._status_raw)">
              {{ item.status }}
            </span>
          </div>
          <div class="mobile-ticket-card-body">
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('ผู้แจ้งซ่อม', 'Reporter') }}</span>
              <span class="mobile-row-val">{{ item.reporter }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('หน่วยงาน/แผนก', 'Department') }}</span>
              <span class="mobile-row-val">{{ item.reporterDept }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('ประเภทงาน', 'Job Type') }}</span>
              <span class="mobile-row-val">{{ item.category }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('วันที่แจ้ง', 'Reported Date') }}</span>
              <span class="mobile-row-val">{{ item.dateInformed }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('SLA', 'SLA') }}</span>
              <span class="mobile-row-val">
                <CBadge :color="item._slaOverdue ? 'danger' : 'success'">{{ item.sla }}</CBadge>
                <small v-if="item._sla_hours" class="ml-1 text-muted">
                  ({{ dashboardText('เป้าหมาย:', 'Target:') }} {{ item._sla_hours }}h)
                </small>
              </span>
            </div>
          </div>
          <button type="button" class="mobile-view-details-btn" @click.stop="$router.push('/admin/mainadmin')">
            {{ dashboardText('ดูรายละเอียด', 'View Details') }}
          </button>
        </div>
        <div v-if="!formattedTickets.length" class="text-center py-4 text-muted">
          {{ dashboardText('ไม่มีข้อมูล', 'No data') }}
        </div>
      </div>
    </div>
    </div>

    <TicketDetailModal v-if="selectedReport" @close="selectedReport=null">
      <ReportDetail :report="selectedReport" @close="selectedReport=null" @refresh="fetchData" />
    </TicketDetailModal>
  </div>
</template>

<script>
import keyboardActivate from '@/directives/keyboardActivate';
import listboxEscape from '@/directives/listboxEscape';
import keyboardSearch from '@/mixins/keyboardSearch';
import CModal from '@/components/common/AccessibleModal.vue';
import axios from 'axios'
import moment from 'moment'
import { CChartBar, CChartPie, CChartDoughnut, CChartLine } from '@coreui/vue-chartjs'
import TicketDetailModal from '@/components/common/TicketDetailModal.vue'
import ReportDetail from './ReportDetail.vue'
import { matchTicketBilingual } from '@/utils/buildingBilingual.js'
import { getTicketLocationFull, getTicketReporterAffiliation } from '@/utils/ticketViewHelper.js'

export default {
  directives: { keyboardActivate, listboxEscape },
  mixins: [keyboardSearch],
  name: 'AdminDashboard',
  components: { CModal,
    CChartBar,
    CChartPie,
    CChartDoughnut,
    CChartLine,
    TicketDetailModal,
    ReportDetail
  },
  data() {
    const today = moment()
    return {
      selectedReport: null,
      selectedRange: 'week',
      activeDashboardTab: 'todo',
      activeWorkloadTab: 'supervisor',
      startDate: today.clone().startOf('isoWeek').format('YYYY-MM-DD'),
      endDate: today.clone().endOf('isoWeek').format('YYYY-MM-DD'),
      searchQuery: '',
      currentMatchIndex: 0,
      showSearchDropdown: false,
      hasNavigatedMatch: false,

      tickets: [],
      categories: [],
      staff: [],
      technicians: [],
      supervisors: [],
      currentRepairWorkloads: [],

      stats: {
        total: 0,
        inProgress: 0,
        completed: 0,
        overdue: 0,
        satisfaction: 0,
        awaitingEvaluation: 0
      },

      selectedTodo: null,
      todoCurrentPage: 1,
      todoItemsPerPage: 5,
      technicianCurrentPage: 1,
      technicianItemsPerPage: 5,
      partsCurrentPage: 1,
      partsItemsPerPage: 5,
      showPartsDetailModal: false,
      selectedPartsTicket: null,
      tableItemsPerPage: 10,
      tableCurrentPage: 1,
      currentTime: Date.now()
    }
  },
  watch: {
    searchQuery(val) {
      this.hasNavigatedMatch = false
      if (val && val.trim()) {
        this.showSearchDropdown = true
        this.currentMatchIndex = 0
      } else {
        this.currentMatchIndex = 0
        this.showSearchDropdown = false
      }
    }
  },
  computed: {
    rangeOptions() {
      return [
        { label: this.dashboardText('สัปดาห์', 'Week'), value: 'week' },
        { label: this.dashboardText('เดือน', 'Month'), value: 'month' },
        { label: this.dashboardText('ปี', 'Year'), value: 'year' },
        { value: 'custom' }
      ]
    },

    filteredTickets() {
      const now = moment()
      let list = this.tickets.filter(t => t && t.status !== 'cancelled')

      list = list.filter(t => {
        const date = moment(t.userPart?.dateTimeInformed || t.createdAt)
        if (!date.isValid()) return true

        switch (this.selectedRange) {
          case 'week': return date.isSame(now, 'isoWeek')
          case 'month': return date.isSame(now, 'month')
          case 'year': return date.isSame(now, 'year')
          case 'all': return true
          case 'custom':
            if (this.startDate && this.endDate) {
              return date.isBetween(moment(this.startDate).startOf('day'), moment(this.endDate).endOf('day'), null, '[]')
            }
            return true
          default: return true
        }
      })

      return list
    },

    searchMatches() {
      if (!this.searchQuery || !this.searchQuery.trim()) return []
      const q = this.searchQuery.trim()
      const allTickets = Array.isArray(this.tickets) ? this.tickets : []
      const matches = allTickets.filter((t) => matchTicketBilingual(t, q))

      return matches.slice(0, 30).map((t, idx) => {
        const ticketNo = t.ticketNumber || (t._id || '').substring(0, 8).toUpperCase() || '-'
        const loc = getTicketLocationFull(t, this.$t, this.$store.state.locale.lang) || '-'
        const rawStatus = (t.status || '').toLowerCase()
        const prob = t.userPart?.problemDetails || '-'
        const reporter = this.getReporterName ? this.getReporterName(t) : (t.userPart?.fullname || t.userPart?.username || '-')
        const badgeColor = this.getBadge ? this.getBadge(rawStatus) : 'ticket'

        return {
          id: t._id || t.ticketNumber || idx,
          ticket: t,
          title: `${ticketNo} · ${loc}`,
          description: prob,
          meta: reporter ? `${this.dashboardText('ผู้แจ้ง', 'Reporter')}: ${reporter}` : '',
          sectionLabel: this.getStatusLabel ? this.getStatusLabel(rawStatus) : rawStatus,
          badgeClass: 'badge-' + badgeColor
        }
      })
    },

    slaWithinCount() {
      return Math.max(0, this.stats.total - this.stats.overdue)
    },

    assignmentOverview() {
      const openTickets = this.filteredTickets.filter(ticket =>
        ['pending', 'assigned', 'in_progress'].includes(ticket.status)
      )
      const unassigned = openTickets.filter(ticket => !this.getActiveAssigneeIds(ticket).length).length
      return {
        openJobs: openTickets.length,
        unassigned,
        assigned: openTickets.length - unassigned
      }
    },

    completionRate() {
      if (!this.stats.total) return 0
      return Math.min(100, Math.max(0, Math.round((this.stats.completed / this.stats.total) * 100)))
    },

    supervisorWorkloadList() {
      const map = new Map()

      this.supervisors.forEach(s => {
        const id = String(this.extractId(s))
        if (id) {
          map.set(id, {
            id,
            name: `${s.name || ''} ${s.surname || ''}`.trim() || s.email || '-',
            section: s.section || s.department
          })
        }
      })

      this.filteredTickets.forEach(t => {
        const sup = t.supervisorPart?.supervisor
        if (sup) {
          const id = String(this.extractId(sup))
          if (id && !map.has(id)) {
            const name = typeof sup === 'object' ? `${sup.name || ''} ${sup.surname || ''}`.trim() || sup.email || '-' : String(sup)
            const section = typeof sup === 'object' ? (sup.department || sup.section || '') : ''
            map.set(id, { id, name, section })
          }
        }
      })

      const result = []
      map.forEach((supData, supId) => {
        const assignedTickets = this.filteredTickets.filter(t =>
          this.getActiveAssigneeIds(t).includes(supId) || String(this.extractId(t.supervisorPart?.supervisor)) === supId
        )

        const acceptedJobs = assignedTickets.filter(t =>
          this.getAssignmentStatusForUser(t, supId) === 'accepted'
        ).length

        const completedJobs = assignedTickets.filter(t =>
          ['done', 'welldone'].includes(t.status)
        ).length

        const activeJobs = assignedTickets.filter(t =>
          ['pending', 'assigned', 'in_progress'].includes(t.status)
        ).length

        const currentWorkload = this.currentRepairWorkloads?.find(worker =>
          String(worker?.personId || worker?._id || worker?.id || '') === supId
        )

        const workloadPercent = Number(currentWorkload?.workloadPercent) || (assignedTickets.length > 0 ? Math.min(100, Math.round((activeJobs / assignedTickets.length) * 100)) : 0)

        result.push({
          ...supData,
          assignedJobs: assignedTickets.length,
          acceptedJobs,
          inProgressJobs: assignedTickets.filter(t => t.status === 'in_progress').length,
          completedJobs,
          workloadPercent,
          completionRate: this.calculateCompletionRate(completedJobs, assignedTickets.length)
        })
      })

      return result.sort((a, b) => b.assignedJobs - a.assignedJobs)
    },

    technicianWorkload() {
      const map = new Map()

      this.technicians.forEach(t => {
        const id = String(this.extractId(t))
        if (id) {
          map.set(id, {
            _id: id,
            name: `${t.name || ''} ${t.surname || ''}`.trim() || t.email || '-'
          })
        }
      })

      this.filteredTickets.forEach(t => {
        const assignees = this.getActiveAssigneeIds(t)
        assignees.forEach(techId => {
          if (techId && !map.has(techId)) {
            const assignedTech = t.supervisorPart?.assignedTechnician
            const name = (assignedTech && typeof assignedTech === 'object')
              ? `${assignedTech.name || ''} ${assignedTech.surname || ''}`.trim() || 'Technician'
              : `Technician`
            map.set(techId, { _id: techId, name })
          }
        })
      })

      const result = []
      map.forEach((techData, techId) => {
        const assignedTickets = this.filteredTickets.filter(t =>
          this.getActiveAssigneeIds(t).includes(techId)
        )

        const activeTickets = assignedTickets.filter(t =>
          ['pending', 'assigned', 'in_progress'].includes(t.status)
        )

        const completedJobs = assignedTickets.filter(t =>
          ['done', 'welldone'].includes(t.status)
        ).length

        const overdueJobs = assignedTickets.filter(t => this.isTicketOverdue(t)).length

        const currentWorkload = this.currentRepairWorkloads?.find(worker =>
          String(worker?.personId || worker?._id || worker?.id || '') === techId
        )

        const workloadPercent = Number(currentWorkload?.workloadPercent) || (assignedTickets.length > 0 ? Math.min(100, Math.round((activeTickets.length / assignedTickets.length) * 100)) : 0)

        result.push({
          _id: techId,
          name: techData.name,
          totalJobs: assignedTickets.length,
          availability: activeTickets.length ? 'Busy' : 'Available',
          availabilityClass: activeTickets.length ? 'is-busy' : 'is-available',
          currentTickets: activeTickets.slice(0, 3).map(ticket => ({
            id: ticket._id || ticket.ticketNumber,
            number: ticket.ticketNumber || ticket._id || '-',
            jobType: ticket?.userPart?.subCategory?.name || ticket?.userPart?.subCategory || '-',
            status: ticket.status,
            overdue: this.isTicketOverdue(ticket),
            sla: this.getSlaCountdown(ticket)
          })),
          remainingCurrentTickets: Math.max(0, activeTickets.length - 3),
          activeJobs: activeTickets.length,
          completedJobs,
          overdueJobs,
          workloadPercent,
          completionRate: this.calculateCompletionRate(completedJobs, assignedTickets.length)
        })
      })

      return result.sort((a, b) => b.totalJobs - a.totalJobs)
    },

    technicianAvailabilitySummary() {
      const list = this.technicianWorkload
      const total = list.length
      const busy = list.filter(t => t.activeJobs > 0).length
      return { total, busy, available: Math.max(0, total - busy) }
    },

    technicianTotalPages() {
      return Math.max(1, Math.ceil(this.technicianWorkload.length / this.technicianItemsPerPage))
    },

    paginatedTechnicianWorkload() {
      const start = (this.technicianCurrentPage - 1) * this.technicianItemsPerPage
      return this.technicianWorkload.slice(start, start + this.technicianItemsPerPage)
    },

    technicianPageNumbers() {
      const total = this.technicianTotalPages
      if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1)
      let start = Math.max(1, this.technicianCurrentPage - 2)
      let end = Math.min(total, start + 4)
      if (end - start < 4) start = Math.max(1, end - 4)
      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    },

    partsTickets() {
      return this.filteredTickets
        .map(ticket => {
          const parts = this.getTicketParts(ticket)
          const noPartsUsed = ticket?.technicianPart?.noPartsUsed === true
          if (!parts.length && !noPartsUsed) return null

          return {
            id: ticket._id || ticket.ticketNumber,
            number: ticket.ticketNumber || ticket._id || '-',
            jobType: ticket?.userPart?.subCategory?.name || ticket?.userPart?.subCategory || '-',
            parts,
            noPartsUsed,
            total: parts.reduce((sum, part) => sum + part.total, 0)
          }
        })
        .filter(Boolean)
    },

    partsSummary() {
      return this.partsTickets.reduce((summary, ticket) => {
        summary.totalCost += ticket.total
        summary.itemCount += ticket.parts.length
        if (ticket.parts.length) summary.ticketsWithParts += 1
        return summary
      }, { totalCost: 0, ticketsWithParts: 0, itemCount: 0 })
    },

    partsTotalPages() {
      return Math.max(1, Math.ceil(this.partsTickets.length / this.partsItemsPerPage))
    },

    paginatedPartsTickets() {
      const start = (this.partsCurrentPage - 1) * this.partsItemsPerPage
      return this.partsTickets.slice(start, start + this.partsItemsPerPage)
    },

    partsPageNumbers() {
      const total = this.partsTotalPages
      if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1)
      let start = Math.max(1, this.partsCurrentPage - 2)
      let end = Math.min(total, start + 4)
      if (end - start < 4) start = Math.max(1, end - 4)
      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    },

    todoList() {
      return this.filteredTickets.filter(t => ['pending', 'assigned', 'in_progress'].includes(t.status))
    },

    todoTotalPages() {
      return Math.max(1, Math.ceil(this.todoList.length / this.todoItemsPerPage))
    },

    paginatedTodoTickets() {
      const start = (this.todoCurrentPage - 1) * this.todoItemsPerPage
      return this.todoList.slice(start, start + this.todoItemsPerPage)
    },

    todoPageNumbers() {
      const total = this.todoTotalPages
      if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1)
      let start = Math.max(1, this.todoCurrentPage - 2)
      let end = Math.min(total, start + 4)
      if (end - start < 4) start = Math.max(1, end - 4)
      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    },

    highlightLabels() {
      const now = moment()
      switch (this.selectedRange) {
        case 'week': return [
          this.dashboardText('จ.', 'Mon'),
          this.dashboardText('อ.', 'Tue'),
          this.dashboardText('พ.', 'Wed'),
          this.dashboardText('พฤ.', 'Thu'),
          this.dashboardText('ศ.', 'Fri'),
          this.dashboardText('ส.', 'Sat'),
          this.dashboardText('อา.', 'Sun')
        ]
        case 'month': return Array.from({ length: now.daysInMonth() }, (_, i) => `${i + 1}`)
        case 'year': return [
          this.dashboardText('ม.ค.', 'Jan'), this.dashboardText('ก.พ.', 'Feb'),
          this.dashboardText('มี.ค.', 'Mar'), this.dashboardText('เม.ย.', 'Apr'),
          this.dashboardText('พ.ค.', 'May'), this.dashboardText('มิ.ย.', 'Jun'),
          this.dashboardText('ก.ค.', 'Jul'), this.dashboardText('ส.ค.', 'Aug'),
          this.dashboardText('ก.ย.', 'Sep'), this.dashboardText('ต.ค.', 'Oct'),
          this.dashboardText('พ.ย.', 'Nov'), this.dashboardText('ธ.ค.', 'Dec')
        ]
        default: return [
          this.dashboardText('ม.ค.', 'Jan'), this.dashboardText('ก.พ.', 'Feb'),
          this.dashboardText('มี.ค.', 'Mar'), this.dashboardText('เม.ย.', 'Apr'),
          this.dashboardText('พ.ค.', 'May'), this.dashboardText('มิ.ย.', 'Jun'),
          this.dashboardText('ก.ค.', 'Jul'), this.dashboardText('ส.ค.', 'Aug'),
          this.dashboardText('ก.ย.', 'Sep'), this.dashboardText('ต.ค.', 'Oct'),
          this.dashboardText('พ.ย.', 'Nov'), this.dashboardText('ธ.ค.', 'Dec')
        ]
      }
    },

    highlightDatasets() {
      const totalData = new Array(this.highlightLabels.length).fill(0)
      const doneData = new Array(this.highlightLabels.length).fill(0)

      this.filteredTickets.forEach(t => {
        const date = moment(t.userPart?.dateTimeInformed || t.createdAt)
        if (!date.isValid()) return

        let index = -1
        switch (this.selectedRange) {
          case 'week': index = date.isoWeekday() - 1; break
          case 'month': index = date.date() - 1; break
          case 'year': index = date.month(); break
          default: index = date.month(); break
        }

        if (index >= 0 && index < totalData.length) {
          totalData[index]++
          if (['done', 'welldone'].includes(t.status)) {
            doneData[index]++
          }
        }
      })

      return [
        {
          label: this.dashboardText('แจ้งซ่อมทั้งหมด', 'Total Requests'),
          backgroundColor: '#1d4ed8',
          borderColor: '#1d4ed8',
          borderWidth: 1,
          borderRadius: 8,
          borderSkipped: false,
          data: totalData
        },
        {
          label: this.dashboardText('ดำเนินการเสร็จสิ้น', 'Completed'),
          backgroundColor: '#059669',
          borderColor: '#059669',
          borderWidth: 1,
          borderRadius: 8,
          borderSkipped: false,
          data: doneData
        }
      ]
    },

    highlightTrendItems() {
      const labels = this.highlightLabels || []
      const datasets = this.highlightDatasets || []
      const totalList = datasets[0]?.data || []
      const doneList = datasets[1]?.data || []
      const maxVal = Math.max(1, ...totalList, ...doneList)
      return labels.map((label, idx) => {
        const total = totalList[idx] || 0
        const done = doneList[idx] || 0
        return {
          label,
          total,
          done,
          totalHeight: Math.max(3, Math.round((total / maxVal) * 150)),
          doneHeight: Math.max(3, Math.round((done / maxVal) * 150))
        }
      })
    },

    highlightOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'top', labels: { boxWidth: 12, usePointStyle: true, fontSize: 12 } },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, precision: 0, suggestedMax: 5 } }]
        }
      }
    },

    donutLabels() {
      return [this.dashboardText('เสร็จสิ้นแล้ว', 'Completed'), this.dashboardText('กำลังดำเนินการ / รอดำเนินการ', 'Active / Pending')]
    },

    donutDatasets() {
      return [{
        backgroundColor: ['#059669', '#1d4ed8'],
        borderColor: ['#ffffff', '#ffffff'],
        borderWidth: 3,
        data: [this.stats.completed, Math.max(0, this.stats.total - this.stats.completed)]
      }]
    },

    donutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 72,
        legend: { display: false }
      }
    },

    summaryData() {
      const summary = {}
      this.filteredTickets.forEach(t => {
        const dept = t.userPart?.category?.departmentName || t.userPart?.department || this.dashboardText('อื่นๆ', 'Other')
        if (!summary[dept]) summary[dept] = { name: dept, total: 0 }
        summary[dept].total++
      })
      return Object.values(summary).sort((a, b) => b.total - a.total)
    },

    pieLabels() {
      return this.summaryData.map(s => s.name)
    },

    pieDatasets() {
      const data = this.summaryData.map(s => s.total)
      const colors = ['#1d4ed8', '#059669', '#3b82f6', '#0d9488', '#f59e0b', '#7c3aed', '#0284c7']
      return [{
        backgroundColor: colors.slice(0, data.length),
        borderColor: '#ffffff',
        borderWidth: 2,
        data
      }]
    },

    pieOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'bottom', labels: { boxWidth: 10, fontSize: 11, usePointStyle: true, padding: 12 } }
      }
    },

    barLabels() {
      return [
        this.dashboardText('ม.ค.', 'Jan'), this.dashboardText('ก.พ.', 'Feb'),
        this.dashboardText('มี.ค.', 'Mar'), this.dashboardText('เม.ย.', 'Apr'),
        this.dashboardText('พ.ค.', 'May'), this.dashboardText('มิ.ย.', 'Jun'),
        this.dashboardText('ก.ค.', 'Jul'), this.dashboardText('ส.ค.', 'Aug'),
        this.dashboardText('ก.ย.', 'Sep'), this.dashboardText('ต.ค.', 'Oct'),
        this.dashboardText('พ.ย.', 'Nov'), this.dashboardText('ธ.ค.', 'Dec')
      ]
    },

    barDatasets() {
      const deptCounts = {}
      const labels = this.barLabels

      this.filteredTickets.forEach(t => {
        const dept = t.userPart?.category?.departmentName || t.userPart?.department || this.dashboardText('อื่นๆ', 'Other')
        if (!deptCounts[dept]) deptCounts[dept] = new Array(labels.length).fill(0)
        const month = moment(t.userPart?.dateTimeInformed || t.createdAt).month()
        if (month >= 0 && month < labels.length) {
          deptCounts[dept][month]++
        }
      })

      const colors = ['#1d4ed8', '#059669', '#3b82f6', '#0d9488', '#f59e0b']
      return Object.keys(deptCounts).slice(0, 5).map((dept, idx) => ({
        label: dept,
        backgroundColor: colors[idx % colors.length],
        borderRadius: 8,
        borderSkipped: false,
        data: deptCounts[dept]
      }))
    },

    barOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'top', labels: { boxWidth: 12, fontSize: 11, usePointStyle: true } },
        scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0, suggestedMax: 5 } }] }
      }
    },

    reportFields() {
      return [
        { key: 'ticketNo', label: this.dashboardText('เลขที่แจ้งซ่อม', 'Ticket No.'), _classes: 'font-weight-bold' },
        { key: 'dateInformed', label: this.dashboardText('วันที่แจ้ง', 'Date Informed') },
        { key: 'reporterName', label: this.dashboardText('ผู้แจ้ง', 'Reporter') },
        { key: 'reporterDepartment', label: this.dashboardText('หน่วยงาน', 'Department') },
        { key: 'category', label: this.dashboardText('ประเภทงาน', 'Category') },
        { key: 'status', label: this.dashboardText('สถานะ', 'Status') },
        { key: 'sla', label: this.dashboardText('SLA', 'SLA') },
        { key: 'assignee', label: this.dashboardText('ผู้รับผิดชอบ', 'Assignee') },
        { key: 'closedDate', label: this.dashboardText('วันที่ปิดงาน', 'Closed Date') }
      ]
    },

    formattedTickets() {
      const flattenCategories = []
      this.categories.forEach(cat => {
        if (cat.subCategories && Array.isArray(cat.subCategories)) {
          cat.subCategories.forEach(sub => {
            flattenCategories.push({
              dep: cat.departmentName,
              categoryId: cat._id || cat.id,
              name: sub.name,
              previousNames: sub.previousNames || [],
              sla: sub.slaHours
            })
          })
        }
      })

      return this.filteredTickets.map(t => {
        let slaHours = 24
        const subName = t.userPart?.subCategory?.name || t.userPart?.subCategory
        const catId = t.userPart?.category?._id || t.userPart?.category?.id || t.userPart?.category || t.categoryId
        const matched = flattenCategories.find(s => {
          if (catId && s.categoryId && String(s.categoryId) !== String(catId)) return false;
          return s.name === subName || (Array.isArray(s.previousNames) && s.previousNames.includes(subName));
        }) || flattenCategories.find(s => s.name === subName || (Array.isArray(s.previousNames) && s.previousNames.includes(subName)))
        if (matched) slaHours = matched.sla

        const isOverdue = this.isTicketOverdue(t)
        const isClosed = ['done', 'welldone'].includes(t.status)
        const displayId = t.ticketNumber || (t.id || t._id || '').substring(0, 8).toUpperCase() || 'N/A'

        let assignee = '-'
        const assignedTech = t.supervisorPart?.assignedTechnician
        const supervisorObj = t.supervisorPart?.supervisor

        if (assignedTech && typeof assignedTech === 'object') {
          assignee = `${assignedTech.name || ''} ${assignedTech.surname || ''}`.trim()
        } else if (supervisorObj && typeof supervisorObj === 'object') {
          assignee = `${supervisorObj.name || ''} ${supervisorObj.surname || ''}`.trim()
        }

        const reporterName = t.userPart?.user
          ? `${t.userPart.user.name || ''} ${t.userPart.user.surname || ''}`.trim()
          : (t.userPart?.fullname || t.userPart?.username || '-')

        const reporterDept = this.getReporterAffiliation(t)

        return {
          ticketNo: displayId,
          dateInformed: this.formatDate(t.userPart?.dateTimeInformed || t.createdAt),
          reporterName,
          reporterDept,
          category: subName || '-',
          status: this.getStatusLabel(t.status),
          sla: isOverdue ? this.dashboardText('เกิน SLA', 'Overdue SLA') : this.dashboardText('ตรงตาม SLA', 'On Time'),
          assignee,
          closedDate: isClosed ? this.formatDate(t.updatedAt) : '-',
          _status_raw: t.status,
          _sla_hours: slaHours,
          _slaOverdue: isOverdue
        }
      })
    }
  },

  mounted() {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.todoItemsPerPage = 3
      this.technicianItemsPerPage = 3
      this.partsItemsPerPage = 3
    }
    this.fetchData()
    document.addEventListener('click', this.handleDocumentClick)
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick)
  },

  methods: {
    removeMatchHighlight() {},
    handleDocumentClick(e) {
      if (this.$refs.searchBoxRef && !this.$refs.searchBoxRef.contains(e.target)) {
        this.showSearchDropdown = false
      }
    },

    clearSearch() {
      this.searchQuery = ''
      this.showSearchDropdown = false
    },

    closeSearchDropdown() {
      this.showSearchDropdown = false
    },

    onSelectSearchResult(m) {
      if (!m || !m.ticket) return
      this.showSearchDropdown = false
      const ticket = m.ticket
      const ticketId = ticket._id || ticket.id || ''
      const ticketNo = ticket.ticketNumber || (ticketId ? ticketId.substring(0, 8).toUpperCase() : '')
      const isFinished = ['done', 'welldone', 'cancelled'].includes(ticket.status)

      if (isFinished) {
        this.$router.push({
          path: '/admin/history',
          query: { ticketId, ticketNo, highlight: 'true' }
        })
      } else {
        const filter = ticket.status === 'pending' ? 'unassigned' : 'active'
        this.$router.push({
          path: '/admin/mainadmin',
          query: { filter, ticketId, ticketNo, highlight: 'true' }
        })
      }
    },

    scrollElementToCenter(target) {
      if (!target) return
      const scrollContainers = [
        document.querySelector('.c-body'),
        document.querySelector('.c-main'),
        document.querySelector('.role-theme-app'),
        document.scrollingElement,
        document.documentElement,
        document.body
      ]
      const container = scrollContainers.find(
        c => c && c.scrollHeight > c.clientHeight && (typeof window === 'undefined' || window.getComputedStyle(c).overflowY !== 'hidden')
      )
      const bottomNavHeight = 90
      const headerHeight = 70

      if (container && container !== document.documentElement && container !== document.body) {
        const containerRect = container.getBoundingClientRect()
        const targetRect = target.getBoundingClientRect()
        const currentScroll = container.scrollTop
        const relativeTargetTop = targetRect.top - containerRect.top + currentScroll
        const effectiveHeight = Math.max(100, containerRect.height - bottomNavHeight)
        const idealTop = relativeTargetTop - (effectiveHeight / 2) + (targetRect.height / 2)

        container.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        })
      } else {
        const targetRect = target.getBoundingClientRect()
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0
        const windowHeight = window.innerHeight || document.documentElement.clientHeight
        const effectiveHeight = Math.max(100, windowHeight - headerHeight - bottomNavHeight)
        const idealTop = (currentScrollY + targetRect.top) - headerHeight - (effectiveHeight / 2) + (targetRect.height / 2)

        window.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        })
      }
    },

    scrollToAndHighlight(ids) {
      const isVisible = (el) => {
        if (!el) return false
        const rect = el.getBoundingClientRect()
        if (rect.width === 0 && rect.height === 0) return false
        if (el.offsetParent === null && (typeof window === 'undefined' || window.getComputedStyle(el).position !== 'fixed')) {
          return false
        }
        return true
      }

      let foundTarget = null
      for (const id of ids) {
        if (!id) continue
        const rawId = String(id).replace(/^ticket-row-|^m-rep-|^todo-|^m-todo-/, '')
        const candidates = [
          document.getElementById('m-todo-' + rawId),
          document.getElementById('m-rep-' + rawId),
          document.getElementById(id),
          document.getElementById('ticket-row-' + rawId),
          document.getElementById('todo-' + rawId),
          ...(this.$el ? Array.from(this.$el.querySelectorAll(`[data-ticket-id="${rawId}"], [data-ticket-no="${rawId}"]`)) : [])
        ].filter(Boolean)

        for (const el of candidates) {
          const target = el.closest('.mobile-ticket-card') ||
                         el.closest('.task-table-row') ||
                         el.closest('tr') ||
                         el
          if (isVisible(target)) {
            target.classList.remove('match-highlight-active')
            void target.offsetWidth
            target.classList.add('match-highlight-active')
            setTimeout(() => {
              target.classList.remove('match-highlight-active')
            }, 3600)
            if (!foundTarget) {
              foundTarget = target
            }
          }
        }
      }

      if (foundTarget) {
        this.scrollElementToCenter(foundTarget)
        return true
      }
      return false
    },

    onSearchEnter() {
      if (this.searchMatches && this.searchMatches.length > 0) {
        this.onSelectSearchResult(this.searchMatches[0])
      }
    },

    dashboardText(thText, enText) {
      const lang = (this.$i18n?.locale || this.$store?.state?.locale?.lang || '').toLowerCase()
      return lang.startsWith('en') ? enText : thText
    },

    goToTechnicianWorkload() {
      this.activeDashboardTab = 'workload'
      this.activeWorkloadTab = 'technician'
      this.$nextTick(() => {
        const el = document.querySelector('.workload-card')
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },

    goToTicketList(filterType) {
      if (filterType === 'pending' || filterType === 'unassigned') {
        this.$router.push({ path: '/admin/mainadmin', query: { filter: 'unassigned' } })
      } else if (filterType === 'in_progress' || filterType === 'active') {
        this.$router.push({ path: '/admin/mainadmin', query: { filter: 'active' } })
      } else if (filterType === 'overdue' || filterType === 'over_sla') {
        this.$router.push({ path: '/admin/mainadmin', query: { filter: 'overdue' } })
      } else if (filterType === 'completed' || filterType === 'done') {
        this.$router.push({ path: '/admin/mainadmin', query: { filter: 'completed' } })
      } else {
        this.$router.push({ path: '/admin/mainadmin', query: { filter: 'all' } })
      }
    },

    getWorkflowStatusClass(status) {
      if (status === 'pending') return 'soft-status-pending'
      if (['done', 'welldone'].includes(status)) return 'soft-status-completed'
      return 'soft-status-active'
    },

    getSlaPillInfo(ticket) {
      if (!ticket) return { text: '-', className: 'sla-pill-normal' }
      if (['done', 'welldone'].includes(ticket.status)) {
        return { text: this.dashboardText('เสร็จสิ้น', 'Completed'), className: 'sla-pill-done' }
      }
      if (['cancelled'].includes(ticket.status)) {
        return { text: this.dashboardText('ยกเลิก', 'Cancelled'), className: 'sla-pill-done' }
      }
      const deadline = this.getTicketSlaDeadline(ticket)
      if (!deadline) return { text: '-', className: 'sla-pill-normal' }

      const now = moment()
      const diffMs = deadline.diff(now)
      if (diffMs <= 0) {
        return { text: this.dashboardText('เกิน SLA', 'Over SLA'), className: 'sla-pill-overdue' }
      }

      const dur = moment.duration(diffMs)
      const hours = Math.floor(dur.asHours())
      const mins = dur.minutes()

      const timeText = this.dashboardText(
        `เหลือ ${hours} ชม. ${mins} นาที`,
        `${hours}h ${mins}m remaining`
      )

      return {
        text: timeText,
        className: hours < 4 ? 'sla-pill-urgent' : 'sla-pill-normal'
      }
    },

    extractArrayFromResponse(response) {
      const payload = response?.data;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.tickets)) return payload.tickets;
      if (Array.isArray(payload?.result)) return payload.result;
      return [];
    },

    formatSection(val) {
      if (!val) return '-'
      if (typeof val === 'object') {
        return val.departmentName || val.name || val.title || val.code || '-'
      }
      if (typeof val === 'string') {
        const trimmed = val.trim()
        if (trimmed.startsWith('{')) {
          try {
            const obj = JSON.parse(trimmed)
            return obj.departmentName || obj.name || obj.title || obj.code || '-'
          } catch (e) {
            return val
          }
        }
        return val
      }
      return '-'
    },

    async fetchData() {
      try {
        const [ticketsRes, categoriesRes, usersRes, workloadRes] = await Promise.all([
          axios.get(`${process.env.VUE_APP_APP_URL}/ticket/admin/all`),
          axios.get(`${process.env.VUE_APP_APP_URL}/category`),
          axios.get(`${process.env.VUE_APP_APP_URL}/user/admin_user`).catch(() => ({ data: [] })),
          axios.get(`${process.env.VUE_APP_APP_URL}/ticket/workload/current`).catch(() => ({ data: [] }))
        ])

        const rawTickets = this.extractArrayFromResponse(ticketsRes)
        this.tickets = rawTickets.sort((a, b) => new Date(b.createdAt || b.userPart?.dateTimeInformed) - new Date(a.createdAt || a.userPart?.dateTimeInformed))

        const rawCategories = this.extractArrayFromResponse(categoriesRes)
        this.categories = rawCategories.length ? rawCategories : (categoriesRes.data?.data || [])

        const usersList = this.extractArrayFromResponse(usersRes)
        this.staff = usersList
        this.technicians = usersList.filter(u => u && u.role === 'technician')
        this.supervisors = usersList.filter(u => u && u.role === 'supervisor')

        const rawWorkload = Array.isArray(workloadRes?.data?.data)
          ? workloadRes.data.data
          : Array.isArray(workloadRes?.data)
            ? workloadRes.data
            : []
        this.currentRepairWorkloads = rawWorkload.map(worker => ({
          ...worker,
          workloadPercent: Number(worker?.workloadPercent) || 0,
          activeTicketCount: Number(worker?.activeTicketCount) || 0
        }))

        this.calculateStats()
      } catch (error) {
        console.error("Error fetching admin dashboard data:", error)
      }
    },

    onRangeSelect(rangeValue) {
      const today = moment()
      if (rangeValue === 'custom') {
        if (this.selectedRange !== 'custom') {
          this.startDate = ''
          this.endDate = ''
        }
        this.selectedRange = 'custom'
        return
      }

      if (rangeValue === 'all') {
        this.startDate = ''
        this.endDate = ''
        this.selectedRange = 'all'
        this.calculateStats()
        return
      }

      switch (rangeValue) {
        case 'week':
          this.startDate = today.clone().startOf('isoWeek').format('YYYY-MM-DD')
          this.endDate = today.clone().endOf('isoWeek').format('YYYY-MM-DD')
          break
        case 'month':
          this.startDate = today.clone().startOf('month').format('YYYY-MM-DD')
          this.endDate = today.clone().endOf('month').format('YYYY-MM-DD')
          break
        case 'year':
          this.startDate = today.clone().startOf('year').format('YYYY-MM-DD')
          this.endDate = today.clone().endOf('year').format('YYYY-MM-DD')
          break
      }
      this.selectedRange = rangeValue
      this.calculateStats()
    },

    onCustomDateChange() {
      if (!this.startDate || !this.endDate) return
      if (moment(this.endDate).isBefore(moment(this.startDate), 'day')) {
        this.endDate = this.startDate
      }
      this.calculateStats()
    },

    calculateStats() {
      const list = this.filteredTickets
      const inProgress = list.filter(t => ['pending', 'assigned', 'in_progress'].includes(t.status)).length
      const completed = list.filter(t => ['done', 'welldone'].includes(t.status)).length
      const overdue = list.filter(t => this.isTicketOverdue(t)).length

      const awaitingEvaluation = list.filter(t => {
        const finished = ['done', 'welldone'].includes(t.status)
        const rating = Number(t.rating?.stars)
        return finished && !(rating > 0)
      }).length

      const rated = list.filter(t => Number(t.rating?.stars) > 0)
      let satisfaction = 0
      if (rated.length) {
        const sum = rated.reduce((acc, t) => acc + Number(t.rating.stars), 0)
        satisfaction = (sum / rated.length).toFixed(1)
      }

      this.stats = {
        total: list.length,
        inProgress,
        completed,
        overdue,
        satisfaction,
        awaitingEvaluation
      }
    },

    extractId(value) {
      if (!value) return ""
      if (typeof value === "string") return value
      if (typeof value === "object") return value._id || value.id || ""
      return ""
    },

    getActiveAssigneeIds(ticket) {
      const assignments = ticket?.supervisorPart?.assignments
      if (Array.isArray(assignments) && assignments.length) {
        return assignments
          .filter(a => a && a.status !== 'rejected')
          .map(a => this.extractId(a.assignee))
          .filter(Boolean)
          .map(String)
      }

      const legacy = this.extractId(ticket?.supervisorPart?.assignedTechnician)
      return legacy ? [String(legacy)] : []
    },

    getAssignmentStatusForUser(ticket, userId) {
      const assignments = ticket?.supervisorPart?.assignments
      if (Array.isArray(assignments) && assignments.length) {
        const assignment = assignments.find(item => item && String(this.extractId(item.assignee)) === String(userId))
        return assignment?.status || ""
      }
      return String(this.extractId(ticket?.supervisorPart?.assignedTechnician)) === String(userId) ? "accepted" : ""
    },

    calculateCompletionRate(completed, total) {
      const tot = Number(total) || 0
      if (tot <= 0) return 0
      return Math.min(100, Math.max(0, Math.round(((Number(completed) || 0) / tot) * 100)))
    },

    isTicketOverdue(ticket) {
      if (['cancelled'].includes(ticket.status)) return false
      const deadline = this.getTicketSlaDeadline(ticket)
      if (!deadline) return false
      const end = ['done', 'welldone'].includes(ticket.status) ? moment(ticket.technicianPart?.dateTimeFinished || ticket.updatedAt) : moment()
      return end.isAfter(deadline)
    },

    getTicketSlaDeadline(ticket) {
      if (ticket?.slaDeadline && moment(ticket.slaDeadline).isValid()) {
        return moment(ticket.slaDeadline)
      }
      const subName = ticket?.userPart?.subCategory?.name || ticket?.userPart?.subCategory
      const start = moment(ticket?.userPart?.dateTimeInformed || ticket?.createdAt)
      if (!start.isValid()) return null

      let slaHours = 24
      this.categories.forEach(cat => {
        if (cat.subCategories && Array.isArray(cat.subCategories)) {
          const sub = cat.subCategories.find(s => s.name === subName)
          if (sub) slaHours = sub.slaHours || 24
        }
      })

      return start.clone().add(slaHours, 'hours')
    },

    getSlaCountdown(ticket) {
      if (['done', 'welldone', 'cancelled'].includes(ticket.status)) {
        return { text: 'COMPLETED', className: 'sla-done' }
      }
      const deadline = this.getTicketSlaDeadline(ticket)
      if (!deadline) return { text: '-', className: 'sla-normal' }

      const now = moment()
      const diffMs = deadline.diff(now)
      if (diffMs <= 0) {
        return { text: 'OVERDUE', className: 'sla-overdue' }
      }

      const dur = moment.duration(diffMs)
      const hours = Math.floor(dur.asHours())
      const mins = dur.minutes()

      return {
        text: `${hours}h ${mins}m`,
        className: hours < 4 ? 'sla-urgent' : 'sla-normal'
      }
    },

    getTicketParts(ticket) {
      const techPart = ticket?.technicianPart || {}
      if (Array.isArray(techPart.parts) && techPart.parts.length) {
        return techPart.parts
          .filter(p => p && String(p.name || '').trim())
          .map(p => {
            const quantity = Math.max(1, Number(p.quantity) || 1)
            const unitPrice = Math.max(0, Number(p.unitPrice) || 0)
            return { name: String(p.name).trim(), quantity, unitPrice, total: quantity * unitPrice }
          })
      }
      const legacyName = String(techPart.spareParts || '').trim()
      if (legacyName && legacyName !== '-') {
        const quantity = Math.max(1, Number(techPart.quantity) || 1)
        const unitPrice = Math.max(0, Number(techPart.cost) || 0)
        return [{ name: legacyName, quantity, unitPrice, total: quantity * unitPrice }]
      }
      if (Number(techPart.cost) > 0) {
        const cost = Number(techPart.cost)
        return [{ name: 'ค่าใช้จ่ายทั่วไป', quantity: 1, unitPrice: cost, total: cost }]
      }
      return []
    },

    openPartsDetail(ticket) {
      this.selectedPartsTicket = ticket
      this.showPartsDetailModal = true
    },

    selectTodo(task) {
      if (window.innerWidth <= 768) {
        this.$router.push('/admin/mainadmin')
        return
      }
      this.selectedTodo = task
    },

    formatCurrency(value) {
      return Number(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    formatDate(value) {
      if (!value) return '-'
      return moment(value).format('DD/MM/YYYY HH:mm')
    },

    getReporterName(task) {
      const user = task?.userPart?.user
      if (user) return `${user.name || ''} ${user.surname || ''}`.trim() || user.username || '-'
      return task?.userPart?.fullname || task?.userPart?.username || '-'
    },

    getReporterAffiliation(task) {
      return getTicketReporterAffiliation(task)
    },

    getReporterDepartment(task) {
      return this.getReporterAffiliation(task)
    },

    getTaskLocation(task) {
      const bldg = task?.userPart?.building
      const bldgName = typeof bldg === 'object' ? bldg.name : (bldg || '')
      const room = task?.userPart?.room || ''
      return [bldgName, room].filter(Boolean).join(' / ') || '-'
    },

    getStatusLabel(status) {
      switch (status) {
        case 'pending': return this.dashboardText('รอรับงาน', 'Pending')
        case 'assigned': return this.dashboardText('มอบหมายช่าง', 'Assigned')
        case 'in_progress': return this.dashboardText('กำลังดำเนินการ', 'In Progress')
        case 'done':
        case 'welldone': return this.dashboardText('ปิดงานแล้ว', 'Completed')
        case 'cancelled': return this.dashboardText('ยกเลิก', 'Cancelled')
        default: return status || '-'
      }
    },

    getBadge(status) {
      switch (status) {
        case 'pending': return 'warning'
        case 'assigned': return 'info'
        case 'in_progress': return 'primary'
        case 'done':
        case 'welldone': return 'success'
        case 'cancelled': return 'secondary'
        default: return 'light'
      }
    }
  }
}
</script>

<style scoped>

.supervisor-dashboard {
  --admin-primary: #1d4ed8;
  --admin-dark: #1e3a8a;
  --success: #059669;
  --warning: #d97706;
  --danger: #dc2626;
  --rating: #f59e0b;
  --admin-indigo: #1e40af;
  --page-bg: #eff6ff;
  --surface: #ffffff;
  --border: #bfdbfe;
  --border-soft: #dbeafe;
  --text: #0f172a;
  --muted: #64748b;

  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0 6px 28px;
  background: #eff6ff !important;
  color: var(--text);
  font-family: "Sarabun", sans-serif;
  font-size: 14px;
  box-sizing: border-box;
}

/* CARDS WRAPPERS */
.work-card,
.analytics-card,
.detail-card,
.report-table-card,
.summary-card,
.overview-panel {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 16px !important;
  border: 1px solid var(--border-soft) !important;
}

/* MAIN ZERO-SCROLL DASHBOARD TABS */
.dashboard-main-tabs {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow-x: auto;
  padding: 6px 4px 12px;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.main-tab-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 22px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e40af;
  background: #ffffff;
  border: 1.5px solid #bfdbfe;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(29, 78, 216, 0.08);

  &:hover {
    background: #eff6ff;
    border-color: #60a5fa;
    color: #1d4ed8;
    transform: translateY(-1px);
  }

  &.active {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%) !important;
    color: #ffffff !important;
    border-color: #1d4ed8 !important;
    box-shadow: 0 4px 16px rgba(29, 78, 216, 0.35) !important;
    font-weight: 800;
  }
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  margin-left: 8px;
  background: #dbeafe;
  color: #1d4ed8;
}

.main-tab-btn.active .tab-badge {
  background: #ffffff;
  color: #1e40af;
}

/* HIGH CONTRAST INDIGO BUTTONS */
.primary-action-button,
.view-all-button,
.mobile-view-details-btn,
.row-action {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%) !important;
  color: #ffffff !important;
  font-weight: 800 !important;
  border: none !important;
  border-radius: 999px !important;
  box-shadow: 0 4px 14px rgba(29, 78, 216, 0.35) !important;
  transition: all 0.2s ease !important;
  cursor: pointer;

  &:hover {
    background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%) !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 18px rgba(30, 58, 138, 0.45) !important;
  }
}

/* TOPBAR & STICKY BLUR ZONE */
.dashboard-sticky-area {
  position: sticky !important;
  top: var(--app-header-height) !important;
  z-index: 100 !important;
  margin: 0 0 16px 0 !important;
  padding: 0 !important;
  background: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  overflow: visible;
}

.dashboard-topbar {
  position: static;
  width: 100%;
  max-width: 100%;
  height: 112px;
  min-height: 112px;
  margin: 0 !important;
  padding: 22px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #1e40af 100%) !important;
  border-radius: 16px !important;
  box-shadow: 0 4px 18px rgba(30, 58, 138, 0.25);
  box-sizing: border-box;
}

.dashboard-page-title {
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #ffffff !important;
  font-size: clamp(1.4rem, 2vw, 1.9rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
}

.dashboard-title-icon,
.dashboard-page-title svg,
::v-deep .dashboard-page-title .c-icon {
  color: #ffffff !important;
  fill: currentColor !important;
  font-size: 1.6rem;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

/* FILTERS */
.dashboard-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
}

.range-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.range-tab {
  padding: 8px 16px;
  font-size: 0.92rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
}

.range-tab:hover {
  background: rgba(255, 255, 255, 0.2);
}

.range-tab.active {
  background: #ffffff;
  color: #1d4ed8;
  font-weight: 800;
  border-color: #ffffff;
}

.custom-date-range {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  padding: 4px 10px;
  border-radius: 8px;
}

.custom-date-range input {
  border: 0;
  font-size: 0.88rem;
  font-weight: 600;
}

/* KPI GRID — SINGLE UNIFIED OCEAN BLUE THEME */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 100%;
}

.summary-card {
  background: #ffffff !important;
  border: 1.5px solid #dbeafe !important;
  padding: 20px 22px !important;
  border-radius: 16px !important;
  box-shadow: 0 2px 8px rgba(30, 58, 138, 0.04) !important;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease !important;
}

.summary-card:hover {
  transform: translateY(-2px) !important;
  border-color: #93c5fd !important;
  box-shadow: 0 6px 18px rgba(30, 58, 138, 0.1) !important;
}

.summary-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summary-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.icon-blue {
  background: #eff6ff !important;
  color: #1e40af !important;
  border: 1px solid #bfdbfe !important;
}

.icon-orange {
  background: #faf5ff !important;
  color: #7c3aed !important;
  border: 1px solid #e9d5ff !important;
}

.icon-red {
  background: #fef2f2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
}

.icon-green {
  background: #f0fdf4 !important;
  color: #059669 !important;
  border: 1px solid #bbf7d0 !important;
}

/* SEGMENTED TAB WORKLOAD */
.workload-tab-segmented {
  display: inline-flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
  gap: 4px;
  border: 1px solid #cbd5e1;
}

.segmented-btn {
  border: none;
  background: transparent;
  padding: 8px 16px;
  font-size: 0.92rem;
  font-weight: 700;
  color: #475569;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: #1e40af;
  }

  &.active {
    background: #ffffff;
    color: #1e40af;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
}

.summary-label {
  color: #0f172a !important;
  font-size: 1.02rem !important;
  font-weight: 800 !important;
  margin-bottom: 6px;
}

.summary-bottom {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.summary-value {
  font-size: 2.3rem !important;
  font-weight: 800 !important;
  color: #0f172a !important;
  line-height: 1;
}

.trend-badge {
  padding: 4px 10px !important;
  border-radius: 999px !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
}

.trend-neutral {
  background: #eff6ff !important;
  color: #1e40af !important;
  border: 1px solid #bfdbfe !important;
}

.trend-active {
  background: #faf5ff !important;
  color: #7c3aed !important;
  border: 1px solid #e9d5ff !important;
}

.trend-down {
  background: #fef2f2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
}

.trend-up {
  background: #f0fdf4 !important;
  color: #059669 !important;
  border: 1px solid #bbf7d0 !important;
}

/* OPERATIONAL SUMMARY */
.operational-summary {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
}

.overview-panel {
  flex: 1;
  min-width: 0;
  background: #ffffff !important;
  padding: 16px 22px !important;
  border-radius: 14px !important;
  border: 2px solid #e0f2fe !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.overview-panel.clickable {
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.overview-panel.clickable:hover {
  border-color: #38bdf8 !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(3, 105, 161, 0.12) !important;
}

.overview-panel.clickable:active {
  transform: translateY(0);
}

.overview-eyebrow {
  display: block;
  font-size: 0.85rem !important;
  color: #0369a1 !important;
  text-transform: uppercase;
  font-weight: 800 !important;
  margin-bottom: 3px;
}

.overview-panel strong {
  font-size: 1.3rem !important;
  color: #0f172a !important;
  font-weight: 800 !important;
}

.overview-metrics {
  display: flex;
  gap: 10px;
}

.overview-metric {
  font-size: 0.92rem !important;
  padding: 6px 14px !important;
  border-radius: 8px !important;
  font-weight: 700 !important;
}

.metric-overdue {
  background: #ffe4e6 !important;
  color: #9f1239 !important;
}

.metric-within {
  background: #d1fae5 !important;
  color: #065f46 !important;
}

.overview-panel-secondary {
  background: #fafafa;
  justify-content: space-around;
  font-size: 0.9rem;
}

/* WORK CARDS & TABLES */
.work-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 18px 20px;
  border: 1px solid var(--border-soft);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.work-card-header,
.analytics-card-header {
  margin-bottom: 12px;
}

.work-card-header h4,
.analytics-card-header h4 {
  margin: 0;
  color: var(--admin-primary);
  font-weight: 700;
  font-size: 1.15rem;
}

.work-card-header p,
.analytics-card-header p {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 0.85rem;
}

.technician-workload-wrapper {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 0;
}

.technician-workload-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 680px;
}

.technician-workload-table th {
  background: #f1f5f9 !important;
  color: #0f172a !important;
  font-weight: 800 !important;
  padding: 12px 14px !important;
  font-size: 0.98rem !important; /* 15.6px - large & bold header */
  border-bottom: 2px solid #cbd5e1 !important;
  text-align: left;
  white-space: nowrap;
}

.technician-workload-table td {
  padding: 12px 14px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  font-size: 0.95rem !important; /* 15.2px - clear cell text */
  color: #1e293b !important;
  font-weight: 600 !important;
  vertical-align: middle;
}

.workload-role {
  display: inline-block;
  background: #e0f2fe !important;
  color: #0369a1 !important;
  padding: 4px 10px !important;
  border-radius: 6px !important;
  font-size: 0.85rem !important;
  font-weight: 800 !important;
}

.workload-section-text {
  color: #0f172a !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
}

.workload-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.workload-progress span {
  flex: 1;
  height: 10px !important;
  background: #e2e8f0 !important;
  border-radius: 999px !important;
  overflow: hidden;
  display: block;
}

.workload-progress span i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #0284c7 0%, #059669 100%) !important;
  transition: width 0.3s;
  border-radius: 999px !important;
}

.technician-availability {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}

.is-available {
  background: #e8f5e9;
  color: #2e7d32;
}

.is-busy {
  background: #fff3e0;
  color: #e65100;
}

.current-ticket-line {
  font-size: 0.8rem;
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.current-ticket-status {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 700;
}

.sla-normal {
  background: #e8f5e9;
  color: #2e7d32;
}

.sla-urgent {
  background: #fff8e1;
  color: #f57f17;
}

.sla-overdue {
  background: #ffebee;
  color: #c62828;
}

.sla-done {
  background: #e0e0e0;
  color: #616161;
}

/* PARTS OVERVIEW TABLE & SUMMARY */
.parts-summary-bottom {
  border-top: 1px solid #eee;
  padding-top: 12px;
  width: 100%;
}

.parts-summary {
  display: flex;
  gap: 16px;
  background: #fdfaf6;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #f5ebe0;
  font-size: 0.85rem;
}

/* =====================================================
   IMPECCABLE PARTS OVERVIEW TABLE & POPUP MODAL
===================================================== */
.parts-workload-table th {
  background: #f8fafc !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 0.85rem !important;
  border-bottom: 2px solid #e2e8f0 !important;
  padding: 12px 16px !important;
}

.parts-workload-table td {
  padding: 12px 16px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f1f5f9 !important;
  font-size: 0.88rem !important;
}

.parts-inline-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  justify-content: center;
}

.parts-inline-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 0.78rem;
  font-weight: 600;
}

.parts-more-count {
  display: inline-block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
}

.parts-qty-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-weight: 700;
  font-size: 0.82rem;
}

.parts-price-val {
  font-weight: 700;
  color: #64748b;
  font-size: 0.92rem;

  &.has-cost {
    color: #0f172a;
  }
}

.parts-detail-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border: 1px solid #bfdbfe !important;
  border-radius: 6px !important;
  color: #1d4ed8 !important;
  background: #eff6ff !important;
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: none !important;
  transition: all 0.15s ease !important;
  text-align: center;

  &:hover {
    background: #1d4ed8 !important;
    border-color: #1d4ed8 !important;
    color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(29, 78, 216, 0.2) !important;
  }
}

.parts-table-footer {
  padding: 14px 18px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 12px 12px;
}

.parts-summary-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.parts-summary-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #000000 !important;

  .chip-label {
    color: #000000 !important;
    font-weight: 700;
  }
  .chip-value {
    color: #000000 !important;
    font-weight: 800;
  }

  &.parts-summary-total {
    background: #eff6ff;
    border-color: #93c5fd;

    .chip-label {
      color: #000000 !important;
      font-weight: 700;
    }
    .chip-value-total {
      color: #000000 !important;
      font-weight: 800;
      font-size: 1rem;
    }
    .chip-unit {
      color: #000000 !important;
      font-weight: 700;
    }
  }
}

/* MODAL STYLING - CLEAN EXECUTIVE HIGH-CONTRAST */
::v-deep .parts-detail-modal .modal-content {
  border: 1px solid #cbd5e1 !important;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16) !important;
}

::v-deep .parts-detail-modal .modal-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 20px 24px !important;
}

.parts-modal-title {
  color: #000000 !important;
  font-size: 1.25rem !important;
  font-weight: 800 !important;
  line-height: 1.3;
}

.parts-modal-subtitle {
  font-size: 0.88rem !important;
  color: #1e293b !important;
  font-weight: 500;
  line-height: 1.3;
}

.parts-modal-close-btn {
  background: transparent;
  border: none;
  font-size: 1.6rem;
  color: #64748b;
  line-height: 1;
  padding: 0 4px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #000000;
  }
}

::v-deep .parts-detail-modal .modal-body {
  padding: 22px 24px !important;
  background: #ffffff !important;
}

::v-deep .parts-detail-modal .modal-footer {
  background: #f8fafc !important;
  border-top: 1px solid #e2e8f0 !important;
  padding: 14px 24px !important;
}

.parts-modal-info-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 14px 20px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  margin-bottom: 20px;
}

.parts-info-col {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.parts-info-label {
  font-size: 0.82rem !important;
  color: #1e293b !important;
  font-weight: 700 !important;
}

.parts-info-value {
  font-size: 1rem !important;
  color: #000000 !important;
  font-weight: 700 !important;

  &.ticket-id {
    color: #000000 !important;
    font-weight: 800 !important;
    font-size: 1.1rem !important;
  }
}

.parts-modal-table-container {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
}

.parts-modal-table {
  width: 100%;
  border-collapse: collapse;

  th {
    background: #f1f5f9 !important;
    color: #000000 !important;
    font-size: 0.88rem !important;
    font-weight: 800 !important;
    padding: 12px 16px !important;
    border-bottom: 2px solid #cbd5e1 !important;
  }

  td {
    padding: 12px 16px !important;
    font-size: 0.92rem !important;
    color: #000000 !important;
    font-weight: 600 !important;
    border-bottom: 1px solid #e2e8f0 !important;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  .th-num, .td-num { width: 6%; text-align: center; color: #000000 !important; font-weight: 700 !important; }
  .th-name, .td-name { width: 44%; color: #000000 !important; font-weight: 700 !important; }
  .th-qty, .td-qty { width: 14%; color: #000000 !important; font-weight: 700 !important; }
  .th-price, .td-price { width: 18%; color: #000000 !important; font-weight: 600 !important; }
  .th-total, .td-total { width: 18%; color: #000000 !important; font-weight: 800 !important; }
}

.parts-modal-empty-state {
  padding: 36px 16px;
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #000000 !important;
}

.parts-modal-summary-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8fafc;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  margin-top: 20px;
}

.parts-summary-title {
  color: #000000 !important;
  font-size: 1.05rem !important;
  font-weight: 800 !important;
}

.parts-summary-amount strong {
  font-size: 1.5rem !important;
  font-weight: 900 !important;
  color: #000000 !important;
}

.parts-currency-unit {
  font-size: 0.95rem !important;
  color: #000000 !important;
  font-weight: 700 !important;
  margin-left: 6px;
}

.parts-modal-close-action-btn {
  background: #dc2626 !important;
  border: 1px solid #b91c1c !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  padding: 8px 28px !important;
  border-radius: 8px !important;
  font-size: 0.95rem !important;
  cursor: pointer;
  transition: all 0.15s ease !important;
  box-shadow: 0 1px 3px rgba(220, 38, 38, 0.3) !important;

  &:hover {
    background: #b91c1c !important;
    border-color: #991b1b !important;
    box-shadow: 0 2px 6px rgba(185, 28, 28, 0.4) !important;
  }
}

/* ANALYTICS */
.analytics-layout {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  align-items: stretch;
}

.analytics-card {
  background: #fff;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid var(--border-soft);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
}

.secondary-chart-card {
  border-radius: 12px !important;
  overflow: hidden;
  background: #fff;
}

.analytics-side {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.performance-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.performance-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.highlight-chart-wrapper {
  height: auto;
  min-height: 280px;
  position: relative;
  width: 100%;
  flex: 1;
}

.donut-wrapper {
  height: 190px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.donut-center {
  position: absolute;
  text-align: center;
  pointer-events: none;
}

.donut-center strong {
  display: block;
  font-size: 1.85rem;
  color: #0f172a;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.donut-center span {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 2px;
}

.donut-breakdown-row {
  border-color: #f1f5f9 !important;
}

.donut-breakdown-item {
  flex: 1;
}

.donut-breakdown-val {
  font-size: 1.1rem;
}

.donut-breakdown-divider {
  width: 1px;
  height: 28px;
  background: #e2e8f0;
}

.secondary-chart-card {
  border-radius: 14px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03) !important;
  overflow: hidden;
  background: #fff;
}

.secondary-chart-card .card-header {
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 14px 20px !important;
  font-size: 0.95rem !important;
  color: #0f172a !important;
}

.chart-card-body {
  padding: 16px 20px !important;
  height: 250px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.pie-chart-wrapper,
.bar-chart-wrapper {
  position: relative;
  height: 210px;
  width: 100%;
}

/* TO-DO & WORK LAYOUT */
.work-layout {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 16px;
  width: 100%;
  max-width: 100%;
}

.work-layout-full {
  grid-template-columns: 1fr;
}

/* STYLED BUTTONS */
.view-all-button {
  background: #f8f9fa;
  border: 1px solid var(--border);
  color: var(--text);
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.view-all-button:hover {
  background: var(--admin-primary);
  color: #ffffff;
  border-color: var(--admin-primary);
}

.row-action {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f0f0f0;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text);
  cursor: pointer;
  transition: all 0.2s ease;
}

.row-action:hover {
  background: var(--admin-primary);
  color: #ffffff;
}

.task-table-wrapper {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.task-table {
  display: flex;
  flex-direction: column;
  min-width: 580px;
}

.task-table-header {
  display: grid;
  grid-template-columns: 1.2fr 1.2fr 2fr 1.5fr 40px;
  padding: 10px 14px;
  background: #f8f9fa;
  font-weight: 700;
  font-size: 0.8rem;
  color: #666;
  border-radius: 6px;
}

.task-table-row {
  display: grid;
  grid-template-columns: 1.2fr 1.2fr 2fr 1.5fr 40px;
  padding: 14px;
  border-bottom: 1px solid #eee;
  align-items: center;
  cursor: pointer;
  transition: background 0.15s;
}

.task-table-row:hover,
.task-table-row.selected {
  background: #fbf8f8;
}

.ticket-number {
  font-weight: 700;
  color: var(--admin-primary);
}

.reporter-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reporter-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.todo-status-cell {
  display: flex;
  align-items: center;
}

.todo-status-badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.soft-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.soft-status-pending {
  background: #fff3e0;
  color: #e65100;
}

.soft-status-active {
  background: #e3f2fd;
  color: #1565c0;
}

.soft-status-completed {
  background: #e8f5e9;
  color: #2e7d32;
}

.sla-status-pill {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.73rem;
  font-weight: 700;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.sla-pill-overdue {
  background: #ffebee;
  color: #d32f2f;
  border: 1px solid #ffcdd2;
}

.sla-pill-urgent {
  background: #fff8e1;
  color: #f57f17;
  border: 1px solid #ffe082;
}

.sla-pill-normal {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.sla-pill-done {
  background: #f5f5f5;
  color: #757575;
  border: 1px solid #e0e0e0;
}

.sla-countdown-badge {
  display: block;
  font-size: 0.7rem;
  margin-top: 2px;
}

.detail-card {
  background: #ffffff;
  border-radius: 16px !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px -2px rgba(2, 132, 199, 0.08);
  padding: 0 !important;
  overflow: hidden;
}

.detail-header {
  background: #ffffff !important;
  padding: 14px 18px !important;
  margin: 0 !important;
  border-bottom: 1px solid #e2e8f0 !important;

  h4 {
    color: #0f172a !important;
    font-size: 0.95rem !important;
    font-weight: 700;
  }

  p, .selected-ticket-number {
    color: #64748b !important;
    font-size: 0.8rem !important;
    margin: 2px 0 0 !important;
    font-weight: 600;
  }

  .soft-status {
    border-radius: 20px;
    padding: 3px 10px;
    font-size: 0.75rem;
    font-weight: 700;
  }
}

.selected-job-content {
  padding: 14px 18px !important;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
}

.detail-block {
  span {
    display: block;
    font-size: 0.68rem;
    color: #64748b;
    text-transform: uppercase;
    font-weight: 700;
    margin-bottom: 2px;
  }

  strong {
    font-size: 0.85rem;
    color: #0f172a;
    font-weight: 700;
  }
}

.problem-block {
  background: #f8fafc;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;

  p {
    margin: 4px 0 0;
    font-size: 0.82rem;
    color: #334155;
    line-height: 1.35;
  }
}

.detail-footer {
  padding: 0 18px 16px !important;
  margin: 0 !important;
}

.primary-action-button {
  width: 100%;
  height: 38px;
  padding: 0 16px;
  background: linear-gradient(135deg, #0284c7 0%, #ec4899 100%);
  color: #ffffff;
  border: 0;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px -2px rgba(2, 132, 199, 0.3);

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px -2px rgba(236, 72, 153, 0.35);
  }
}

.primary-action-button:hover {
  background: var(--admin-dark);
}

/* PAGINATION */
.todo-pagination {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

.todo-page-button {
  padding: 5px 12px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.15s;
}

.todo-page-button:hover:not(:disabled) {
  background: #f0f0f0;
}

.todo-page-button.active {
  background: var(--admin-primary);
  color: #fff;
  border-color: var(--admin-primary);
}

.todo-page-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.chart-card-body {
  height: 380px;
  position: relative;
  width: 100%;
}

.pie-chart-wrapper,
.bar-chart-wrapper {
  height: 100%;
  width: 100%;
  position: relative;
}

.highlight-chart-wrapper>div,
.bar-chart-wrapper>div,
.pie-chart-wrapper>div {
  height: 100% !important;
  width: 100% !important;
  position: relative;
}

/* Force Chart.js canvas to fill wrapper height */
.highlight-chart-wrapper canvas,
.bar-chart-wrapper canvas,
.pie-chart-wrapper canvas {
  width: 100% !important;
  height: 100% !important;
}

/* MEDIA QUERIES (MOBILE RESPONSIVE ENHANCEMENTS) */
@media (max-width: 992px) {
  .performance-card {
    min-height: 430px;
  }

  .analytics-layout {
    grid-template-columns: 1fr !important;
    width: 100% !important;
  }

  .work-layout {
    grid-template-columns: 1fr !important;
    width: 100% !important;
  }

  .analytics-side {
    width: 100% !important;
    max-width: 100% !important;
  }
}

@media (max-width: 768px) {
  .secondary-chart-card {
    border-radius: 8px !important;
  }

  .performance-card {
    height: auto !important;
    min-height: 0 !important;
  }

  .performance-card .performance-content {
    flex: none;
    height: 380px;
    min-height: 380px;
  }

  .admin-dashboard-root .mb-3 {
    margin-bottom: 12px !important;
  }

  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 12px 0 !important;
    padding: 0 !important;
    background: transparent !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  .secondary-chart-col:not(:last-child) {
    margin-bottom: 12px;
  }

  .admin-dashboard-root {
    min-height: auto !important;
    padding: 0 4px 18px !important;
  }

  .supervisor-dashboard {
    padding: 12px 0 20px !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .dashboard-topbar {
    position: static;
    margin-bottom: 0 !important;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    height: auto;
    min-height: auto;
    padding: 12px 14px;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
    box-shadow: 0 2px 6px rgba(150, 51, 43, 0.10);
  }

  .dashboard-page-title {
    font-size: 1.15rem;
    gap: 8px;
  }

  .dashboard-title-icon,
  .dashboard-page-title svg,
  ::v-deep .dashboard-page-title .c-icon {
    width: 20px;
    height: 20px;
    font-size: 1.2rem;
  }

  .dashboard-filter {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    justify-content: flex-start;
    padding-bottom: 4px;
    box-sizing: border-box;
  }

  .range-tabs {
    flex-wrap: nowrap;
    width: 100%;
    overflow-x: auto;
  }

  .range-tab {
    white-space: nowrap;
    flex-shrink: 0;
    font-size: 0.75rem;
    padding: 5px 10px;
  }

  .custom-date-range {
    flex-wrap: nowrap;
  }

  .custom-date-range input {
    width: 100px;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 10px !important;
    margin-top: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .summary-card {
    padding: 14px 16px;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
    overflow: hidden;
  }

  .operational-summary {
    flex-direction: column;
    gap: 10px;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .overview-panel {
    min-width: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .overview-metrics {
    flex-wrap: wrap;
  }

  .work-card,
  .analytics-card,
  .detail-card,
  .report-table-card,
  .supervisor-workload-card,
  .technician-workload-card,
  .parts-overview-card {
    padding: 14px !important;
    border-radius: 8px;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
    overflow: hidden;
  }

  .analytics-layout {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .work-layout {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .technician-workload-wrapper {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
    overflow-x: auto;
  }

  .report-table-wrapper {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .task-table-wrapper {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .highlight-chart-wrapper,
  .donut-wrapper,
  .pie-chart-wrapper,
  .bar-chart-wrapper,
  .chart-card-body {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
    overflow: hidden;
  }

  .performance-card {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .detail-grid {
    grid-template-columns: 1fr !important;
  }

  .parts-summary-bottom {
    flex-direction: column-reverse;
    align-items: flex-end !important;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .parts-summary {
    width: 100%;
    max-width: 100%;
    justify-content: space-around;
    flex-wrap: wrap;
    font-size: 0.8rem;
    box-sizing: border-box;
  }

  .todo-pagination {
    flex-wrap: wrap;
    justify-content: center;
  }

  .selected-job-content {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    word-break: break-word;
  }

  .technician-workload-card .technician-workload-table {
    min-width: 760px;
  }

  .technician-workload-card .technician-availability,
  .technician-workload-card .current-ticket-status {
    white-space: nowrap;
  }

  .technician-workload-card .current-ticket-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .technician-workload-card .current-ticket-line>span:first-child {
    min-width: 0;
  }

  .technician-workload-card .current-ticket-status {
    flex-shrink: 0;
    width: auto;
  }
}

@media (max-width: 480px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 8px !important;
  }

  .summary-card {
    padding: 10px 12px;
  }

  .summary-icon {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }

  .summary-value {
    font-size: 1.5rem;
  }

  .overview-panel {
    padding: 12px 14px !important;
  }

  .overview-metrics {
    gap: 6px;
  }

  .overview-metric {
    font-size: 0.75rem;
    padding: 3px 6px;
  }

  .parts-summary {
    gap: 8px;
    font-size: 0.75rem;
    padding: 6px 10px;
  }
}

@media (max-width: 360px) {
  .kpi-grid {
    grid-template-columns: 1fr !important;
  }
}
</style>

